import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
// recharts removed — brand vs generic artık keyword-bazlı card layout kullanıyor
import {
  Printer,
  Download,
  Loader2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Target,
  BarChart3,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  Calendar,
  Globe,
  Users,
  Sparkles,
  Eye,
  Mail,
  Send,
  X,
  CheckCircle,
} from 'lucide-react';

// ─── Utilities ────────────────────────────────────────────────────────────────

const getBrandId = () =>
  typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;

const getBrandName = () => {
  if (typeof window === 'undefined') return '';
  // currentBrandName her zaman set edilmiyor; selectedBrand JSON'ından da dene
  const direct = localStorage.getItem('currentBrandName');
  if (direct) return direct;
  try {
    const stored = localStorage.getItem('selectedBrand');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.name || '';
    }
  } catch { /* ignore */ }
  return '';
};

const API_BASE = 'https://api.glowytics.com';

// Hafta hesaplama yardımcıları
const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
};

const getSunday = (mon) => {
  const d = new Date(mon);
  d.setDate(d.getDate() + 6);
  return d;
};

// Yerel saat dilimine göre YYYY-MM-DD üret. toISOString UTC'ye çevirip
// Istanbul'daki "1 Mart 00:00" → "28 Şubat 21:00 UTC" gibi kaymalara yol açıyor.
const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const fmtDate = (d) => {
  const date = new Date(d);
  const months = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const getWeekRanges = () => {
  const today = new Date();
  const thisMonday = getMonday(today);
  const thisSunday = getSunday(thisMonday);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(lastMonday.getDate() - 7);
  const lastSunday = new Date(lastMonday);
  lastSunday.setDate(lastSunday.getDate() + 6);
  const twoWeeksMonday = new Date(lastMonday);
  twoWeeksMonday.setDate(twoWeeksMonday.getDate() - 7);
  const twoWeeksSunday = new Date(twoWeeksMonday);
  twoWeeksSunday.setDate(twoWeeksSunday.getDate() + 6);
  return {
    this_week: { start: ymd(thisMonday), end: ymd(thisSunday), label: `Bu Hafta (${fmtDate(thisMonday)} - ${fmtDate(thisSunday)})` },
    last_week: { start: ymd(lastMonday), end: ymd(lastSunday), label: `Geçen Hafta (${fmtDate(lastMonday)} - ${fmtDate(lastSunday)})` },
    two_weeks_ago: { start: ymd(twoWeeksMonday), end: ymd(twoWeeksSunday), label: `2 Hafta Önce (${fmtDate(twoWeeksMonday)} - ${fmtDate(twoWeeksSunday)})` },
  };
};

const getMonthRanges = () => {
  const today = new Date();
  const monthStart = (offset) => new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const monthEnd = (offset) => new Date(today.getFullYear(), today.getMonth() + offset + 1, 0);
  const thisStart = monthStart(0);
  // "Bu Ay" = ay başından bugüne (MTD); ay sonu yerine bugünü kullan
  const thisEnd = today;
  const lastStart = monthStart(-1);
  const lastEnd = monthEnd(-1);
  const twoStart = monthStart(-2);
  const twoEnd = monthEnd(-2);
  return {
    this_month: { start: ymd(thisStart), end: ymd(thisEnd), label: `Bu Ay (${fmtDate(thisStart)} - ${fmtDate(thisEnd)})` },
    last_month: { start: ymd(lastStart), end: ymd(lastEnd), label: `Geçen Ay (${fmtDate(lastStart)} - ${fmtDate(lastEnd)})` },
    two_months_ago: { start: ymd(twoStart), end: ymd(twoEnd), label: `2 Ay Önce (${fmtDate(twoStart)} - ${fmtDate(twoEnd)})` },
  };
};

const MONTH_RANGE_KEYS = new Set(['this_month', 'last_month', 'two_months_ago']);
const WEEK_RANGE_KEYS = new Set(['this_week', 'last_week', 'two_weeks_ago']);

const apiFetch = (path, brandId) =>
  fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Brand-ID': brandId,
    },
  });

const fmt = (value, style = 'currency') => {
  const num = Number(value) || 0;
  if (style === 'currency') {
    if (num >= 1_000_000) return `₺${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `₺${(num / 1_000).toFixed(1)}K`;
    return `₺${Math.round(num).toLocaleString('tr-TR')}`;
  }
  if (style === 'number') {
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return Math.round(num).toLocaleString('tr-TR');
  }
  if (style === 'roas') return `${num.toFixed(2)}x`;
  if (style === 'percent') return `%${num.toFixed(2)}`;
  return num;
};

const pct = (current, previous) => {
  if (!previous || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

const exportCSV = (data, columns, filename, dateLabel) => {
  const escapeField = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };
  const lines = [];
  // Tarih bilgisi başlık satırı
  if (dateLabel) {
    lines.push(`"Rapor Dönemi: ${dateLabel}"`);
    lines.push(`"Oluşturulma: ${new Date().toLocaleString('tr-TR')}"`);
    lines.push('');
  }
  lines.push(columns.map((c) => c.label).join(','));
  data.forEach((row) => {
    lines.push(
      columns.map((c) => {
        const val = typeof c.accessor === 'function' ? c.accessor(row) : row[c.accessor];
        return escapeField(val);
      }).join(',')
    );
  });
  const csv = lines.join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const generateCsvContent = (data, columns, dateLabel) => {
  const escapeField = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };
  const lines = [];
  if (dateLabel) {
    lines.push(`"Rapor Dönemi: ${dateLabel}"`);
    lines.push(`"Oluşturulma: ${new Date().toLocaleString('tr-TR')}"`);
    lines.push('');
  }
  lines.push(columns.map((c) => c.label).join(','));
  data.forEach((row) => {
    lines.push(
      columns.map((c) => {
        const val = typeof c.accessor === 'function' ? c.accessor(row) : row[c.accessor || c.key];
        return escapeField(val);
      }).join(',')
    );
  });
  return lines.join('\n');
};

// ─── Action engine & flag helpers ────────────────────────────────────────────
// Deterministic, rule-based recommendations. Used in tables and as fallback for AI.

// Returns a quick "warning badge" for a row (no spend/zero-sale/low ROAS).
const getRowFlag = (spend, revenue, orders) => {
  const s = Number(spend) || 0;
  const r = Number(revenue) || 0;
  const o = Number(orders) || 0;
  if (s === 0) return null;
  const roas = r / s;
  if (o === 0 && s > 100) return { type: 'red', label: '🔴 0 Satış' };
  if (roas < 1) return { type: 'red', label: '🔴 ROAS<1' };
  if (roas < 3) return { type: 'amber', label: '🟡 ROAS<3' };
  return null;
};

// Detect brand search/PMAX campaigns by name (used to skip artır/azalt/durdur recommendations).
// Brand campaigns are volume-bound: budget can't grow demand, pausing leaks ready demand to competitors.
const isBrandCampaign = (name = '', brandName = '') => {
  const n = String(name || '').toLowerCase();
  const bn = String(brandName || '').toLowerCase().trim();
  // Generic "brand" keyword in campaign name (e.g. "Brand Search", "Search-Brand")
  if (/\bbrand\b/.test(n) || /marka/.test(n)) return true;
  if (!bn) return false;
  // Brand name + Turkish-character normalised variants
  const variants = [
    bn,
    bn.replace(/\s+/g, ''),
    bn.replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ç/g, 'c')
      .replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ı/g, 'i'),
  ];
  return variants.some((v) => v && n.includes(v));
};

// Campaign-level action recommendation (Search/PMAX/Shopping/Display/Video).
// `name` + `brandName` are optional but recommended — they let us detect brand campaigns
// (which must NOT receive artır / azalt / durdur recommendations because they are volume-bound).
const getCampaignAction = ({ spend, revenue, orders, clicks, name = '', brandName = '' }) => {
  const s = Number(spend) || 0;
  const r = Number(revenue) || 0;
  const o = Number(orders) || 0;
  const c = Number(clicks) || 0;
  if (s === 0) return null;
  // Brand campaigns: passive label, no artır/azalt/durdur — they spend as much as their volume allows.
  if (isBrandCampaign(name, brandName)) {
    return { code: 'brand_volume', label: '⚪ Hacim takibi', color: 'gray' };
  }
  const roas = r / s;
  if (o === 0 && s > 200) {
    if (c > 50) return { code: 'landing_kontrol', label: '🔴 Landing kontrol', color: 'red' };
    if (c > 10) return { code: 'kelime_temizle', label: '🔴 Keyword temizle', color: 'red' };
    return { code: 'test_et', label: '🟡 Test et', color: 'amber' };
  }
  if (roas >= 5) return { code: 'artir', label: '🟢 Artır', color: 'emerald' };
  if (roas >= 3) return { code: 'koru', label: '🟢 Koru', color: 'green' };
  if (roas >= 2) return { code: 'azalt', label: '🟡 Azalt', color: 'amber' };
  if (roas >= 1) return { code: 'azalt', label: '🟡 Bütçe düşür', color: 'amber' };
  return { code: 'durdur', label: '🔴 Durdur', color: 'red' };
};

// Creative-level action recommendation (Meta ad/creative).
const getCreativeAction = ({ spend, revenue, purchases }) => {
  const s = Number(spend) || 0;
  const r = Number(revenue) || 0;
  const p = Number(purchases) || 0;
  if (s === 0) return null;
  const roas = r / s;
  if (p === 0 && s > 100) return { code: 'kreatif_degistir', label: '🔴 Yeni varyasyon', color: 'red' };
  if (roas >= 5) return { code: 'artir', label: '🟢 Ölçeklendir', color: 'emerald' };
  if (roas >= 3) return { code: 'koru', label: '🟢 Koru', color: 'green' };
  if (roas >= 2) return { code: 'azalt', label: '🟡 Bütçe düşür', color: 'amber' };
  if (roas >= 1) return { code: 'kreatif_degistir', label: '🟡 Varyasyon dene', color: 'amber' };
  return { code: 'durdur', label: '🔴 Durdur', color: 'red' };
};

// Keyword cleanup action (for zero-sales table).
// Intent-aware keyword cleanup recommender.
// IMPORTANT: For brand/flagship-product keywords (e.g. "iphone 17", "macbook pro",
// "<brand> iphone"), recommending "close" after a single 7-day window is fundamentally
// wrong — these are core revenue drivers and 0 conversion in one week usually means
// landing page / stock / price / tracking issue, NOT keyword failure.
const getKeywordCleanupAction = ({ spend, clicks, keywordText = '', brandName = '' }) => {
  const s = Number(spend) || 0;
  const c = Number(clicks) || 0;
  if (s === 0) return null;
  const text = String(keywordText || '').toLowerCase();
  const bn = String(brandName || '').toLowerCase().trim();

  // Brand mention?
  const brandVariants = bn ? [bn, bn.replace(/\s+/g, ''),
    bn.replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ç/g, 'c').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ı/g, 'i')]
    : [];
  const isBrand = brandVariants.some((v) => v && text.includes(v));

  // Specific Apple product model? (flagship/high-intent product names)
  const PRODUCT_RX = /(iphone|macbook|ipad|airpod|imac|magic|airtag|pencil|studio\s?display|\bwatch\b|mac\s?mini|mac\s?pro|mac\s?studio)/i;
  const isProduct = PRODUCT_RX.test(text);

  // High-intent: brand match OR specific product model OR commercial intent words
  const COMMERCIAL_RX = /(satin\s?al|sipari|fiyat|ucuz|indirim|kampanya|nere|magaza|stok)/i;
  const isHighIntent = isBrand || isProduct || COMMERCIAL_RX.test(text);

  if (isHighIntent) {
    // NEVER recommend "close" for high-intent keywords.
    if (c === 0) return { label: 'Eşleme türü kontrol — kapatma', color: 'amber' };
    if (c < 30) return { label: 'İzle (yüksek niyetli) — 2 hafta gözlem', color: 'amber' };
    // High click + 0 sale → almost certainly landing/stock/price/tracking issue
    return { label: 'Stok / landing / fiyat / tracking kontrol', color: 'red' };
  }

  // Low-intent / generic category keywords — aggressive cleanup is OK
  if (c === 0) return { label: 'Eşleme türünü değiştir veya kapat', color: 'red' };
  if (c < 10) return { label: 'Test et (1 hafta daha izle)', color: 'amber' };
  if (c <= 30) return { label: 'Eşleştirme/landing kontrol et', color: 'amber' };
  return { label: 'Kapat veya bid düşür', color: 'red' };
};

// Render an action object as a colored badge JSX.
const ActionBadge = ({ action }) => {
  if (!action) return <span className="text-gray-300 text-xs">—</span>;
  const colorMap = {
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    green: 'bg-green-100 text-green-700 border-green-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    red: 'bg-red-100 text-red-700 border-red-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  const cls = colorMap[action.color] || colorMap.amber;
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded border ${cls} whitespace-nowrap`}>
      {action.label}
    </span>
  );
};

const FlagBadge = ({ flag }) => {
  if (!flag) return <span className="text-gray-300 text-xs">—</span>;
  const cls = flag.type === 'red'
    ? 'bg-red-50 text-red-700 border-red-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded border ${cls} whitespace-nowrap`}>
      {flag.label}
    </span>
  );
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="animate-spin text-indigo-500" size={32} />
  </div>
);

const ErrorFallback = ({ message }) => (
  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
    <AlertCircle size={16} />
    <span>{message || 'Veri yüklenemedi.'}</span>
  </div>
);

const DeltaBadge = ({ value }) => {
  if (value === null || value === undefined || isNaN(value)) return null;
  const positive = value >= 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded ${
        positive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
      }`}
    >
      {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
      {Math.abs(value).toFixed(1)}%
    </span>
  );
};

const SummaryCard = ({ label, value, delta, icon: Icon, color = 'indigo' }) => {
  const colorMap = {
    indigo: 'bg-indigo-50 text-indigo-600',
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    violet: 'bg-violet-50 text-violet-600',
    rose: 'bg-rose-50 text-rose-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-2 shadow-sm print:shadow-none print:border-gray-300">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</span>
        {Icon && (
          <span className={`p-1.5 rounded-lg ${colorMap[color] || colorMap.indigo}`}>
            <Icon size={14} />
          </span>
        )}
      </div>
      <div className="text-xl font-bold text-gray-800">{value}</div>
      {delta !== undefined && <DeltaBadge value={delta} />}
    </div>
  );
};

// Sortable table with pagination
function SortableTable({
  rows,
  columns,
  filename,
  pageSize = 25,
  initialSort,
  isLoading,
  error,
  dateLabel,
}) {
  const [sort, setSort] = useState(initialSort || { key: columns[0]?.key, dir: 'desc' });
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    if (!rows || !rows.length) return [];
    const col = columns.find((c) => c.key === sort.key);
    return [...rows].sort((a, b) => {
      const va = col?.accessor ? col.accessor(a) : a[sort.key];
      const vb = col?.accessor ? col.accessor(b) : b[sort.key];
      const na = Number(va) || 0;
      const nb = Number(vb) || 0;
      if (!isNaN(na) && !isNaN(nb)) return sort.dir === 'desc' ? nb - na : na - nb;
      return sort.dir === 'desc'
        ? String(vb).localeCompare(String(va))
        : String(va).localeCompare(String(vb));
    });
  }, [rows, sort, columns]);

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
    setPage(1);
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorFallback message={error} />;
  if (!rows || rows.length === 0)
    return <p className="text-sm text-gray-400 py-4">Veri bulunamadı.</p>;

  return (
    <div>
      <div className="flex justify-end mb-2 print:hidden">
        <button
          onClick={() => {
            const dateSuffix = dateLabel ? `_${dateLabel.replace(/[^a-zA-Z0-9]/g, '-')}` : '';
            const finalName = filename.replace('.csv', `${dateSuffix}.csv`);
            exportCSV(rows, columns.map((c) => ({ label: c.label, accessor: c.accessor || c.key })), finalName, dateLabel);
          }}
          className="inline-flex items-center gap-1.5 text-xs bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 px-3 py-1.5 rounded-lg transition-colors"
        >
          <Download size={12} />
          CSV İndir
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                  className={`px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap ${
                    col.sortable !== false ? 'cursor-pointer hover:text-gray-900 select-none' : ''
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.sortable !== false && sort.key === col.key ? (
                      sort.dir === 'desc' ? (
                        <ChevronDown size={12} />
                      ) : (
                        <ChevronUp size={12} />
                      )
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map((row, i) => (
              <tr
                key={i}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                {columns.map((col) => {
                  const rawVal =
                    typeof col.accessor === 'function'
                      ? col.accessor(row)
                      : row[col.key];
                  const displayVal =
                    col.render ? col.render(rawVal, row) : rawVal ?? '-';
                  return (
                    <td
                      key={col.key}
                      className={`px-3 py-2 text-gray-700 ${
                        col.numeric ? 'text-right tabular-nums' : ''
                      }`}
                    >
                      {displayVal}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 print:hidden">
          <span className="text-xs text-gray-500">
            {sorted.length} kayıttan {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, sorted.length)} gösteriliyor
          </span>
          <div className="flex items-center gap-1">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Önceki
            </button>
            <span className="text-xs text-gray-600 px-2">
              {page} / {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Sonraki
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section wrapper ───────────────────────────────────────────────────────────
const Section = ({ title, children, className = '', dateLabel }) => (
  <section
    className={`mb-8 print:mb-6 print:break-inside-avoid ${className}`}
  >
    <div className="flex items-center justify-between pb-2 border-b border-gray-200 mb-4">
      <h2 className="text-base font-bold text-gray-800 print:text-sm">
        {title}
      </h2>
      {dateLabel && (
        <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
          {dateLabel}
        </span>
      )}
    </div>
    {children}
  </section>
);

// ─── Campaign × Item helpers ─────────────────────────────────────────────────
const PLATFORM_HINTS = {
  google: ['google', 'youtube', 'doubleclick', 'adwords'],
  meta: ['facebook', 'instagram', 'fb', 'ig', 'meta'],
  tiktok: ['tiktok'],
};

function detectPlatform(source = '', medium = '') {
  const s = String(source || '').toLowerCase();
  const m = String(medium || '').toLowerCase();
  if (PLATFORM_HINTS.google.some((k) => s.includes(k))) return 'google';
  if (PLATFORM_HINTS.meta.some((k) => s.includes(k))) return 'meta';
  if (PLATFORM_HINTS.tiktok.some((k) => s.includes(k))) return 'tiktok';
  return 'other';
}

function filterCampaignItemRows(rows, platform, query) {
  let out = Array.isArray(rows) ? rows : [];
  if (platform && platform !== 'all') {
    out = out.filter((r) => detectPlatform(r.source, r.medium) === platform);
  }
  const q = String(query || '').trim().toLowerCase();
  if (q) {
    out = out.filter((r) =>
      String(r.campaignName || '').toLowerCase().includes(q) ||
      String(r.itemName || '').toLowerCase().includes(q)
    );
  }
  return out;
}

const CampaignItemTable = ({ rows, platformFilter, query }) => {
  const filtered = filterCampaignItemRows(rows, platformFilter, query);

  // Group by campaign, sum revenue per group; rows under it sorted by revenue desc
  const grouped = filtered.reduce((acc, r) => {
    const key = `${r.source}||${r.medium}||${r.campaignName}`;
    if (!acc[key]) {
      acc[key] = {
        source: r.source,
        medium: r.medium,
        campaignName: r.campaignName,
        platform: detectPlatform(r.source, r.medium),
        revenue: 0,
        units: 0,
        items: [],
      };
    }
    acc[key].revenue += r.revenue;
    acc[key].units += r.units;
    acc[key].items.push(r);
    return acc;
  }, {});

  const groups = Object.values(grouped).sort((a, b) => b.revenue - a.revenue);

  const totals = filtered.reduce(
    (acc, r) => {
      acc.revenue += r.revenue;
      acc.units += r.units;
      return acc;
    },
    { revenue: 0, units: 0 }
  );

  const fmtTL = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
  const fmtN = (n) => Number(n || 0).toLocaleString('tr-TR');

  if (groups.length === 0) {
    return <p className="text-sm text-gray-400 py-4">Filtreye uygun satır yok.</p>;
  }

  return (
    <div className="overflow-x-auto rounded border border-gray-200">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-gray-600 uppercase tracking-wider">Platform</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-600 uppercase tracking-wider">Kampanya / Ürün</th>
            <th className="px-3 py-2 text-right font-semibold text-gray-600 uppercase tracking-wider">Ciro</th>
            <th className="px-3 py-2 text-right font-semibold text-gray-600 uppercase tracking-wider">Adet</th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {groups.map((g) => {
            const items = [...g.items].sort((a, b) => b.revenue - a.revenue);
            return (
              <React.Fragment key={`${g.source}-${g.medium}-${g.campaignName}`}>
                <tr className="bg-gray-50 border-t border-gray-200 font-semibold">
                  <td className="px-3 py-2 text-gray-800">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-gray-200 text-gray-700">
                      {g.source} / {g.medium}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-900">{g.campaignName}</td>
                  <td className="px-3 py-2 text-right text-gray-900">{fmtTL(g.revenue)}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{fmtN(g.units)}</td>
                </tr>
                {items.map((it, idx) => (
                  <tr key={`${g.campaignName}-${it.itemName}-${idx}`} className="border-t border-gray-100">
                    <td className="px-3 py-1.5"></td>
                    <td className="px-3 py-1.5 text-gray-700 pl-6">{it.itemName}</td>
                    <td className="px-3 py-1.5 text-right text-gray-700">{fmtTL(it.revenue)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-500">{fmtN(it.units)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot className="bg-gray-900 text-white">
          <tr>
            <td className="px-3 py-2 font-bold" colSpan={2}>TOPLAM ({groups.length} kampanya · {filtered.length} ürün satırı)</td>
            <td className="px-3 py-2 text-right font-bold">{fmtTL(totals.revenue)}</td>
            <td className="px-3 py-2 text-right font-bold">{fmtN(totals.units)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ─── Privileged brand IDs (Gürgençler + Yenilio) ─────────────────────────────
// Bu brand'ler için Ali Bey'e yazılan yorum/mail içeriği rapora gömülü gözükür.
// Diğer brand'ler için yorumlar render edilmez — onlar temiz raporu görür.
const PRIVILEGED_BRAND_IDS = new Set([
  '526a6404-f685-44cd-b57f-27d692845b82', // Gürgençler
  'c7778265-8ac6-4697-ae89-2f4da315d0ac', // Yenilio
]);

// ─── Commentary block helper (sadece privileged brand'lerde render eder) ────
const Commentary = ({ title, children, tone = 'blue' }) => {
  const tones = {
    blue:   { wrap: 'bg-blue-50 border-blue-500',     head: 'text-blue-900',   body: 'text-blue-900/90' },
    amber:  { wrap: 'bg-amber-50 border-amber-500',   head: 'text-amber-900',  body: 'text-amber-900/90' },
    rose:   { wrap: 'bg-rose-50 border-rose-500',     head: 'text-rose-900',   body: 'text-rose-900/90' },
    emerald:{ wrap: 'bg-emerald-50 border-emerald-500', head: 'text-emerald-900', body: 'text-emerald-900/90' },
    gray:   { wrap: 'bg-gray-50 border-gray-400',     head: 'text-gray-800',   body: 'text-gray-700' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div className={`mt-4 border-l-4 rounded-r-lg p-5 ${t.wrap}`}>
      {title && <h4 className={`text-sm font-bold mb-2 ${t.head}`}>{title}</h4>}
      <div className={`text-sm leading-relaxed space-y-2 ${t.body}`}>{children}</div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────────
export default function WeeklyExecutiveReport() {
  const brandId = getBrandId();
  const brandName = getBrandName();
  const showCommentary = PRIVILEGED_BRAND_IDS.has(brandId);

  // ── Date range state ─────────────────────────────────────────────────────────
  const weekRanges = useMemo(() => getWeekRanges(), []);
  const monthRanges = useMemo(() => getMonthRanges(), []);
  const [dateRange, setDateRange] = useState('last_week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // ── Data state ───────────────────────────────────────────────────────────────
  const [unifiedData, setUnifiedData] = useState([]);
  const [unifiedLoading, setUnifiedLoading] = useState(true);
  const [unifiedError, setUnifiedError] = useState(null);

  const [googleCampaigns, setGoogleCampaigns] = useState([]);
  const [googleCampaignsLoading, setGoogleCampaignsLoading] = useState(true);
  const [googleCampaignsError, setGoogleCampaignsError] = useState(null);

  const [metaCampaigns, setMetaCampaigns] = useState([]);
  const [metaCampaignsLoading, setMetaCampaignsLoading] = useState(true);
  const [metaCampaignsError, setMetaCampaignsError] = useState(null);

  const [keywords, setKeywords] = useState([]);
  const [keywordsLoading, setKeywordsLoading] = useState(true);
  const [keywordsError, setKeywordsError] = useState(null);

  const [creatives, setCreatives] = useState([]);
  const [creativesLoading, setCreativesLoading] = useState(true);
  const [creativesError, setCreativesError] = useState(null);
  const [creativeDetailsMap, setCreativeDetailsMap] = useState({});

  const [ga4Data, setGa4Data] = useState([]);
  const [ga4Loading, setGa4Loading] = useState(true);
  const [ga4Error, setGa4Error] = useState(null);

  // Compare period data
  const [compareGa4Data, setCompareGa4Data] = useState([]);
  const [compareUnifiedData, setCompareUnifiedData] = useState([]);

  // brandVsGeneric state kaldırıldı — artık brandVsGenericKeywords (keyword-derived) kullanılıyor

  const [itemSummary, setItemSummary] = useState([]);
  const [itemSummaryLoading, setItemSummaryLoading] = useState(true);
  const [itemSummaryError, setItemSummaryError] = useState(null);

  // ── Campaign × Item × Revenue (LIVE GA4 Data API) ────────────────────────────
  const [campaignItemRows, setCampaignItemRows] = useState([]);
  const [campaignItemLoading, setCampaignItemLoading] = useState(false);
  const [campaignItemError, setCampaignItemError] = useState(null);
  const [campaignItemFetchedAt, setCampaignItemFetchedAt] = useState(null);
  const [campaignItemPlatformFilter, setCampaignItemPlatformFilter] = useState('all'); // 'all' | 'google' | 'meta' | 'tiktok'
  const [campaignItemQuery, setCampaignItemQuery] = useState('');

  // ── Device Analysis (LIVE GA4) ──────────────────────────────────────────────
  const [deviceRows, setDeviceRows] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceError, setDeviceError] = useState(null);

  // ── New vs Returning Revenue (LIVE GA4) ─────────────────────────────────────
  const [newVsReturning, setNewVsReturning] = useState(null);
  const [newVsReturningLoading, setNewVsReturningLoading] = useState(false);
  const [newVsReturningError, setNewVsReturningError] = useState(null);

  // AI executive summary
  const [aiSummary, setAiSummary] = useState('');
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  // (Removed: Meta iframe creative previews — replaced by custom thumbnail-based cards
  //  to avoid Facebook's third-party cookie consent overlay)

  // Brand vs Generic AI analysis
  const [bvgAnalysis, setBvgAnalysis] = useState('');
  const [bvgAnalysisLoading, setBvgAnalysisLoading] = useState(false);

  // Product line growth AI analysis
  const [plAnalysis, setPlAnalysis] = useState('');
  const [plAnalysisLoading, setPlAnalysisLoading] = useState(false);

  // Email state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('efeemrebabacan@gmail.com');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null); // { success, message }

  // plInventory kaldırıldı — stok verisi kullanılmıyor

  // ── Build query string for date range ────────────────────────────────────────
  const { dateQuery, activeDateLabel, activeStartDate, activeEndDate } = useMemo(() => {
    if (dateRange === 'custom' && customStart && customEnd) {
      return {
        dateQuery: `dateRange=custom&startDate=${customStart}&endDate=${customEnd}`,
        activeDateLabel: `${fmtDate(customStart)} - ${fmtDate(customEnd)}`,
        activeStartDate: customStart,
        activeEndDate: customEnd,
      };
    }
    const weekInfo = weekRanges[dateRange];
    if (weekInfo) {
      return {
        dateQuery: `dateRange=custom&startDate=${weekInfo.start}&endDate=${weekInfo.end}`,
        activeDateLabel: `${fmtDate(weekInfo.start)} - ${fmtDate(weekInfo.end)}`,
        activeStartDate: weekInfo.start,
        activeEndDate: weekInfo.end,
      };
    }
    const monthInfo = monthRanges[dateRange];
    if (monthInfo) {
      return {
        dateQuery: `dateRange=custom&startDate=${monthInfo.start}&endDate=${monthInfo.end}`,
        activeDateLabel: `${fmtDate(monthInfo.start)} - ${fmtDate(monthInfo.end)}`,
        activeStartDate: monthInfo.start,
        activeEndDate: monthInfo.end,
      };
    }
    // last30 vb. fallback
    return {
      dateQuery: `dateRange=${dateRange}`,
      activeDateLabel: dateRange === 'last30' ? 'Son 30 Gün' : dateRange,
      activeStartDate: null,
      activeEndDate: null,
    };
  }, [dateRange, customStart, customEnd, weekRanges, monthRanges]);

  // Period mode: 'week' | 'month' | 'other' — başlık ve email konusu için
  const periodMode = useMemo(() => {
    if (WEEK_RANGE_KEYS.has(dateRange)) return 'week';
    if (MONTH_RANGE_KEYS.has(dateRange)) return 'month';
    if (dateRange === 'custom' && customStart && customEnd) {
      const days = Math.round((new Date(customEnd) - new Date(customStart)) / 86400000) + 1;
      if (days >= 25 && days <= 35) return 'month';
      if (days >= 5 && days <= 9) return 'week';
    }
    return 'other';
  }, [dateRange, customStart, customEnd]);

  const periodLabelTr = periodMode === 'month' ? 'Aylık' : periodMode === 'week' ? 'Haftalık' : 'Dönemsel';

  // ── Compare date range (previous period of same length) ────────────────────
  const { compareDateQuery, compareDateLabel } = useMemo(() => {
    if (!activeStartDate || !activeEndDate) return { compareDateQuery: null, compareDateLabel: null };
    const start = new Date(activeStartDate);
    const end = new Date(activeEndDate);
    const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - days + 1);
    return {
      compareDateQuery: `dateRange=custom&startDate=${ymd(prevStart)}&endDate=${ymd(prevEnd)}`,
      compareDateLabel: `${fmtDate(prevStart)} - ${fmtDate(prevEnd)}`,
    };
  }, [activeStartDate, activeEndDate]);

  // ── Fetch helpers ─────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!brandId) return;

    // GA4 site-wide revenue
    setGa4Loading(true);
    setGa4Error(null);
    apiFetch(`/api/google-ads/ga-summary-daily?${dateQuery}`, brandId)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        const rows = d?.rows ?? (Array.isArray(d) ? d : []);
        setGa4Data(rows);
      })
      .catch(() => setGa4Error('GA4 verisi yüklenemedi.'))
      .finally(() => setGa4Loading(false));

    // Unified
    setUnifiedLoading(true);
    setUnifiedError(null);
    apiFetch(`/api/google-ads/unified-campaign-performance?${dateQuery}`, brandId)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setUnifiedData(Array.isArray(d) ? d : []))
      .catch(() => setUnifiedError('Unified veri yüklenemedi.'))
      .finally(() => setUnifiedLoading(false));

    // Google campaigns
    setGoogleCampaignsLoading(true);
    setGoogleCampaignsError(null);
    apiFetch(`/api/google-ads/combined-campaign-performance?${dateQuery}`, brandId)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setGoogleCampaigns(Array.isArray(d) ? d : []))
      .catch(() => setGoogleCampaignsError('Google kampanya verisi yüklenemedi.'))
      .finally(() => setGoogleCampaignsLoading(false));

    // Meta campaigns
    setMetaCampaignsLoading(true);
    setMetaCampaignsError(null);
    apiFetch(`/api/google-ads/meta-campaign-performance?${dateQuery}`, brandId)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        const rows = Array.isArray(d) ? d : (d?.rawData || []);
        setMetaCampaigns(rows);
      })
      .catch(() => setMetaCampaignsError('Meta kampanya verisi yüklenemedi.'))
      .finally(() => setMetaCampaignsLoading(false));

    // Keywords
    setKeywordsLoading(true);
    setKeywordsError(null);
    apiFetch(`/api/google-ads/keyword-full-performance?${dateQuery}`, brandId)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setKeywords(Array.isArray(d) ? d : []))
      .catch(() => setKeywordsError('Keyword verisi yüklenemedi.'))
      .finally(() => setKeywordsLoading(false));

    // Creatives — creative_id bazında aggregate + details (thumbnail)
    setCreativesLoading(true);
    setCreativesError(null);
    apiFetch(`/api/google-ads/meta-ad-performance-with-creatives?${dateQuery}`, brandId)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(async (d) => {
        const rows = d?.rows ?? (Array.isArray(d) ? d : []);
        // creative_id bazında aggregate
        const agg = {};
        for (const r of rows) {
          const cid = r.creative_id || '(unknown)';
          if (!agg[cid]) {
            agg[cid] = { creative_id: cid, spend: 0, clicks: 0, impressions: 0, purchases: 0, revenue: 0, ad_name: r.ad_name || '', campaign_name: r.campaign_name || '' };
          }
          agg[cid].spend += Number(r.spend || r.cost || 0);
          agg[cid].clicks += Number(r.clicks || 0);
          agg[cid].impressions += Number(r.impressions || 0);
          agg[cid].purchases += Number(r.conversions || r.purchase_conversions || 0);
          agg[cid].revenue += Number(r.conversion_value || r.purchase_value || 0);
        }
        const list = Object.values(agg).sort((a, b) => b.spend - a.spend);
        setCreatives(list);

        // creative details (thumbnail, title, body) çek
        // 1) Önce local DB'den toplu çek (hızlı, tüm kreatifler)
        // 2) Sonra top 20 (satış bazlı) için live endpoint ile eksik thumbnail'leri tamamla
        const ids = list.map((c) => c.creative_id).filter((id) => id && id !== '(unknown)');
        if (ids.length > 0) {
          try {
            const chunkSize = 200;
            const allDetails = [];
            for (let i = 0; i < ids.length; i += chunkSize) {
              const chunk = ids.slice(i, i + chunkSize);
              const q = encodeURIComponent(chunk.join(','));
              const detResp = await apiFetch(`/api/google-ads/meta-creative-details?creativeIds=${q}`, brandId);
              if (detResp.ok) {
                const detData = await detResp.json();
                if (Array.isArray(detData?.rows)) allDetails.push(...detData.rows);
              }
            }
            const map = {};
            allDetails.forEach((dd) => { if (dd?.creative_id) map[dd.creative_id] = dd; });
            setCreativeDetailsMap(map);

            // 2) Live thumbnail fallback for top 20 by purchases
            const top20 = list
              .filter((c) => c.creative_id && c.creative_id !== '(unknown)')
              .sort((a, b) => b.purchases - a.purchases)
              .slice(0, 20)
              .map((c) => c.creative_id);
            const needsLive = top20.filter((id) => {
              const det = map[id];
              const has = det && (det.best_media_url || det.image_url || det.video_thumbnail_url || det.thumbnail_url || det.enhanced_image_url);
              return !has;
            });
            if (needsLive.length > 0) {
              try {
                const liveQ = encodeURIComponent(needsLive.join(','));
                const liveResp = await apiFetch(`/api/meta-ads/live/creative-thumbs?creativeIds=${liveQ}`, brandId);
                if (liveResp.ok) {
                  const liveData = await liveResp.json();
                  if (Array.isArray(liveData?.rows)) {
                    setCreativeDetailsMap((prev) => {
                      const next = { ...prev };
                      liveData.rows.forEach((dd) => {
                        if (dd?.creative_id) {
                          next[dd.creative_id] = { ...(next[dd.creative_id] || {}), ...dd };
                        }
                      });
                      return next;
                    });
                  }
                }
              } catch { /* ignore live fallback errors */ }
            }
          } catch { setCreativeDetailsMap({}); }
        }
      })
      .catch(() => setCreativesError('Kreatif verisi yüklenemedi.'))
      .finally(() => setCreativesLoading(false));

    // Brand vs Generic artık keyword verisinden derive ediliyor — ayrı API çağrısına gerek yok

    // Item summary
    setItemSummaryLoading(true);
    setItemSummaryError(null);
    apiFetch(`/api/google-ads/item-performance-summary?${dateQuery}&limit=500`, brandId)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setItemSummary(Array.isArray(d) ? d : []))
      .catch(() => setItemSummaryError('Ürün verisi yüklenemedi.'))
      .finally(() => setItemSummaryLoading(false));

    // Campaign × Item × Revenue — LIVE GA4 API (only when explicit date range available)
    if (activeStartDate && activeEndDate) {
      setCampaignItemLoading(true);
      setCampaignItemError(null);
      apiFetch(
        `/api/google-analytics/ga4/campaign-item-breakdown?startDate=${activeStartDate}&endDate=${activeEndDate}&limit=10000`,
        brandId
      )
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => {
          setCampaignItemRows(Array.isArray(d?.rows) ? d.rows : []);
          setCampaignItemFetchedAt(new Date());
        })
        .catch((err) => {
          console.error('[campaign-item-breakdown] fetch error:', err);
          setCampaignItemError('Canlı GA4 verisi alınamadı.');
          setCampaignItemRows([]);
        })
        .finally(() => setCampaignItemLoading(false));

      // Device breakdown
      setDeviceLoading(true);
      setDeviceError(null);
      apiFetch(
        `/api/google-analytics/ga4/device-breakdown?startDate=${activeStartDate}&endDate=${activeEndDate}`,
        brandId
      )
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => setDeviceRows(Array.isArray(d?.rows) ? d.rows : []))
        .catch((err) => {
          console.error('[device-breakdown] error:', err);
          setDeviceError('Cihaz verisi alınamadı.');
          setDeviceRows([]);
        })
        .finally(() => setDeviceLoading(false));

      // New vs Returning revenue
      setNewVsReturningLoading(true);
      setNewVsReturningError(null);
      apiFetch(
        `/api/google-analytics/ga4/new-vs-returning-revenue?dateRange=custom&startDate=${activeStartDate}&endDate=${activeEndDate}`,
        brandId
      )
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => setNewVsReturning(d || null))
        .catch((err) => {
          console.error('[new-vs-returning] error:', err);
          setNewVsReturningError('Yeni vs dönen müşteri verisi alınamadı.');
          setNewVsReturning(null);
        })
        .finally(() => setNewVsReturningLoading(false));
    } else {
      setCampaignItemRows([]);
      setCampaignItemError('Bu metrik için tarih aralığı gereklidir (haftalık veya özel).');
      setDeviceRows([]);
      setNewVsReturning(null);
    }

    // ── Compare period fetches (GA4 + Unified) ───────────────────────────────
    if (compareDateQuery) {
      apiFetch(`/api/google-ads/ga-summary-daily?${compareDateQuery}`, brandId)
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => {
          const rows = d?.rows ?? (Array.isArray(d) ? d : []);
          setCompareGa4Data(rows);
        })
        .catch(() => setCompareGa4Data([]));

      apiFetch(`/api/google-ads/unified-campaign-performance?${compareDateQuery}`, brandId)
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => setCompareUnifiedData(Array.isArray(d) ? d : []))
        .catch(() => setCompareUnifiedData([]));
    } else {
      setCompareGa4Data([]);
      setCompareUnifiedData([]);
    }
  }, [brandId, dateQuery, compareDateQuery, activeStartDate, activeEndDate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Derived: Executive summary from unified data ───────────────────────────
  const { googleSummary, metaSummary } = useMemo(() => {
    const google = { spend: 0, revenue: 0, orders: 0, clicks: 0 };
    const meta = { spend: 0, revenue: 0, orders: 0, clicks: 0 };

    unifiedData.forEach((row) => {
      // Google metrics
      const gCost = Number(row.google_cost || 0);
      const gRevenue = Number(row.ga4_total_revenue || 0);
      const gOrders = Number(row.ga4_purchases || 0);
      const gClicks = Number(row.google_clicks || 0);
      if (gCost > 0 || gRevenue > 0) {
        google.spend += gCost;
        google.revenue += gRevenue;
        google.orders += gOrders;
        google.clicks += gClicks;
      }
      // Meta metrics
      const mCost = Number(row.meta_cost || 0);
      const mRevenue = Number(row.meta_conversion_value || 0);
      const mOrders = Number(row.meta_conversions || 0);
      const mClicks = Number(row.meta_clicks || 0);
      if (mCost > 0 || mRevenue > 0) {
        meta.spend += mCost;
        meta.revenue += mRevenue;
        meta.orders += mOrders;
        meta.clicks += mClicks;
      }
    });

    const enrich = (d) => {
      const roas = d.spend > 0 ? d.revenue / d.spend : 0;
      const cr = d.clicks > 0 ? (d.orders / d.clicks) * 100 : 0;
      const aov = d.orders > 0 ? d.revenue / d.orders : 0;
      return { ...d, roas, cr, aov };
    };

    return { googleSummary: enrich(google), metaSummary: enrich(meta) };
  }, [unifiedData]);

  // ── Derived: Compare period — Google & Meta summaries ─────────────────────
  const { compareGoogleSummary, compareMetaSummary } = useMemo(() => {
    const google = { spend: 0, revenue: 0, orders: 0, clicks: 0 };
    const meta = { spend: 0, revenue: 0, orders: 0, clicks: 0 };
    compareUnifiedData.forEach((row) => {
      const gCost = Number(row.google_cost || 0);
      const gRevenue = Number(row.ga4_total_revenue || 0);
      const gOrders = Number(row.ga4_purchases || 0);
      const gClicks = Number(row.google_clicks || 0);
      if (gCost > 0 || gRevenue > 0) { google.spend += gCost; google.revenue += gRevenue; google.orders += gOrders; google.clicks += gClicks; }
      const mCost = Number(row.meta_cost || 0);
      const mRevenue = Number(row.meta_conversion_value || 0);
      const mOrders = Number(row.meta_conversions || 0);
      const mClicks = Number(row.meta_clicks || 0);
      if (mCost > 0 || mRevenue > 0) { meta.spend += mCost; meta.revenue += mRevenue; meta.orders += mOrders; meta.clicks += mClicks; }
    });
    const enrich = (d) => {
      const roas = d.spend > 0 ? d.revenue / d.spend : 0;
      const cr = d.clicks > 0 ? (d.orders / d.clicks) * 100 : 0;
      const aov = d.orders > 0 ? d.revenue / d.orders : 0;
      return { ...d, roas, cr, aov };
    };
    return { compareGoogleSummary: enrich(google), compareMetaSummary: enrich(meta) };
  }, [compareUnifiedData]);

  // ── Derived: GA4 summary (site-wide) ──────────────────────────────────────
  const ga4Summary = useMemo(() => {
    const totals = { revenue: 0, transactions: 0, sessions: 0, users: 0 };
    ga4Data.forEach((row) => {
      totals.revenue += Number(row.revenue || 0);
      totals.transactions += Number(row.transactions || 0);
      totals.sessions += Number(row.sessions || 0);
      totals.users += Number(row.users || 0);
    });
    const cr = totals.sessions > 0 ? (totals.transactions / totals.sessions) * 100 : 0;
    const aov = totals.transactions > 0 ? totals.revenue / totals.transactions : 0;
    return { ...totals, cr, aov };
  }, [ga4Data]);

  // ── Derived: Compare period — GA4 summary ─────────────────────────────────
  const compareGa4Summary = useMemo(() => {
    const totals = { revenue: 0, transactions: 0, sessions: 0, users: 0 };
    compareGa4Data.forEach((row) => {
      totals.revenue += Number(row.revenue || 0);
      totals.transactions += Number(row.transactions || 0);
      totals.sessions += Number(row.sessions || 0);
      totals.users += Number(row.users || 0);
    });
    const cr = totals.sessions > 0 ? (totals.transactions / totals.sessions) * 100 : 0;
    const aov = totals.transactions > 0 ? totals.revenue / totals.transactions : 0;
    return { ...totals, cr, aov };
  }, [compareGa4Data]);

  // ─��� Derived: Combined campaign table rows (grouped by campaign name) ─────���
  // Lookup map for Google campaign type metadata.
  // We still fetch combined-campaign-performance, but ONLY use it for the
  // campaignType (Search/PMAX/Shopping/Display/Video) per campaign name.
  // All NUMBERS come from unifiedData (GA4 attribution) for consistency.
  const googleCampaignTypeLookup = useMemo(() => {
    const m = new Map();
    googleCampaigns.forEach((r) => {
      const name = r.campaignName || r.campaign_name;
      const rawType = r.campaignType || r.campaign_type || '';
      const cleanType = String(rawType).replace(/^AdvertisingChannelType\./, '');
      if (name && !m.has(name)) m.set(name, cleanType);
    });
    return m;
  }, [googleCampaigns]);

  // SOURCE OF TRUTH: unifiedData for Google rows (GA4 attribution),
  // metaCampaigns for Meta rows (Meta native attribution).
  // This makes the top summary `googleSummary` and this table agree perfectly.
  const combinedCampaigns = useMemo(() => {
    const map = new Map();
    const addRow = (name, platform, type, spend, revenue, orders, clicks, impressions) => {
      const key = `${platform}::${name}`;
      const existing = map.get(key);
      if (existing) {
        existing.spend += spend;
        existing.revenue += revenue;
        existing.orders += orders;
        existing.clicks += clicks;
        existing.impressions += impressions;
      } else {
        map.set(key, { name, platform, type, spend, revenue, orders, clicks, impressions });
      }
    };

    // Google rows from unifiedData (GA4 attribution)
    unifiedData.forEach((row) => {
      const name = row.campaign_name || '-';
      const gCost = Number(row.google_cost || 0);
      const gRevenue = Number(row.ga4_total_revenue || 0);
      const gOrders = Number(row.ga4_purchases || 0);
      const gClicks = Number(row.google_clicks || 0);
      const gImpr = Number(row.google_impressions || 0);
      if (gCost > 0 || gOrders > 0 || gRevenue > 0) {
        addRow(
          name,
          'Google',
          googleCampaignTypeLookup.get(name) || '-',
          gCost,
          gRevenue,
          gOrders,
          gClicks,
          gImpr
        );
      }
    });

    const mRaw = Array.isArray(metaCampaigns) ? metaCampaigns : (metaCampaigns?.rawData || []);
    mRaw.forEach((r) => {
      addRow(
        r.campaignName || r.campaign_name || '-',
        'Meta',
        r.campaignObjective || r.campaign_objective || '-',
        Number(r.spend || 0),
        Number(r.purchaseValue || r.purchase_value || 0),
        Number(r.purchaseConversions || r.purchase_conversions || 0),
        Number(r.clicks || 0),
        Number(r.impressions || 0)
      );
    });

    return Array.from(map.values()).map((r) => ({
      ...r,
      roas: r.spend > 0 ? r.revenue / r.spend : 0,
      cr: r.clicks > 0 ? (r.orders / r.clicks) * 100 : 0,
      aov: r.orders > 0 ? r.revenue / r.orders : 0,
      flag: getRowFlag(r.spend, r.revenue, r.orders),
      action: getCampaignAction({
        spend: r.spend,
        revenue: r.revenue,
        orders: r.orders,
        clicks: r.clicks,
        name: r.name,
        // Only Google brand search/PMAX is volume-bound; Meta has no equivalent → pass brand only for Google rows
        brandName: r.platform === 'Google' ? brandName : '',
      }),
    }));
  }, [unifiedData, metaCampaigns, googleCampaignTypeLookup, brandName]);

  // ── Derived: Keywords grouped by keyword_text ──────────────────────────────
  const keywordsGrouped = useMemo(() => {
    const map = new Map();
    keywords.forEach((r) => {
      const key = r.keyword_text || '-';
      const existing = map.get(key);
      const cost = Number(r.cost || 0);
      const convValue = Number(r.conversion_value || 0);
      const clicks = Number(r.clicks || 0);
      const impressions = Number(r.impressions || 0);
      const conversions = Number(r.conversions || 0);
      if (existing) {
        existing.cost += cost;
        existing.conversion_value += convValue;
        existing.clicks += clicks;
        existing.impressions += impressions;
        existing.conversions += conversions;
      } else {
        map.set(key, {
          keyword_text: key,
          campaign_name: r.campaign_name || '-',
          ad_group_name: r.ad_group_name || '-',
          cost,
          conversion_value: convValue,
          clicks,
          impressions,
          conversions,
        });
      }
    });
    return Array.from(map.values());
  }, [keywords]);

  // ── Derived: Creatives grouped by ad_name ─────────────────────────────────
  // creatives zaten fetch'te creative_id bazında aggregate edildi

  // ── Derived: Brand vs Generic keyword split ────────────────────────────────
  const brandVsGenericKeywords = useMemo(() => {
    if (!keywordsGrouped.length) return null;
    // Marka adı varyasyonlarını oluştur
    const bn = brandName.toLowerCase().trim();
    if (!bn) return null;
    const variants = [bn, bn.replace(/\s+/g, ''), bn.replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ç/g, 'c').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ı/g, 'i')];
    // Ayrıca kısa varyantları da ekle
    if (bn.length > 4) variants.push(bn.slice(0, 4));

    const brand = { spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0, count: 0, keywords: [] };
    const generic = { spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0, count: 0, keywords: [] };

    keywordsGrouped.forEach((kw) => {
      const kwText = (kw.keyword_text || '').toLowerCase();
      const isBrand = variants.some((v) => kwText.includes(v));
      const target = isBrand ? brand : generic;
      target.spend += Number(kw.cost || 0);
      target.revenue += Number(kw.conversion_value || 0);
      target.orders += Number(kw.conversions || 0);
      target.clicks += Number(kw.clicks || 0);
      target.impressions += Number(kw.impressions || 0);
      target.count += 1;
      if (target.keywords.length < 10) target.keywords.push(kw); // top örnekler
    });

    const enrich = (d) => ({
      ...d,
      roas: d.spend > 0 ? d.revenue / d.spend : 0,
      cr: d.clicks > 0 ? (d.orders / d.clicks) * 100 : 0,
      aov: d.orders > 0 ? d.revenue / d.orders : 0,
      ctr: d.impressions > 0 ? (d.clicks / d.impressions) * 100 : 0,
      spendShare: 0,
      revenueShare: 0,
    });

    const b = enrich(brand);
    const g = enrich(generic);
    const totalSpend = b.spend + g.spend;
    const totalRevenue = b.revenue + g.revenue;
    b.spendShare = totalSpend > 0 ? (b.spend / totalSpend) * 100 : 0;
    g.spendShare = totalSpend > 0 ? (g.spend / totalSpend) * 100 : 0;
    b.revenueShare = totalRevenue > 0 ? (b.revenue / totalRevenue) * 100 : 0;
    g.revenueShare = totalRevenue > 0 ? (g.revenue / totalRevenue) * 100 : 0;

    return { brand: b, generic: g, totalKeywords: keywordsGrouped.length, brandName: bn };
  }, [keywordsGrouped, brandName]);

  // ── Derived: Keyword intent segmentation (High / Medium / Low) ───────────
  // High intent: brand keywords OR explicit purchase intent words
  // Medium intent: contains a model/product word but no brand
  // Low intent: pure category words
  const keywordsByIntent = useMemo(() => {
    if (!keywordsGrouped.length) return null;
    const HIGH = /(satin\s?al|sipari|fiyat|ucuz|indirim|kampanya|nere|magaza|stok|alinir|al\b)/i;
    const MED = /(iphone|macbook|ipad|airpod|watch|imac|mini|pro|max|plus|ultra|magic|airtag|pencil)/i;
    const bn = (brandName || '').toLowerCase().trim();
    const brandVariants = bn
      ? [bn, bn.replace(/\s+/g, ''), bn.replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ç/g, 'c').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ı/g, 'i')]
      : [];
    if (bn.length > 4) brandVariants.push(bn.slice(0, 4));

    const buckets = {
      high:   { label: 'Yüksek Niyet', color: 'emerald', keywords: [], spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0 },
      medium: { label: 'Orta Niyet',   color: 'amber',   keywords: [], spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0 },
      low:    { label: 'Düşük Niyet',  color: 'rose',    keywords: [], spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0 },
    };

    keywordsGrouped.forEach((kw) => {
      const t = (kw.keyword_text || '').toLowerCase();
      const isBrand = brandVariants.length > 0 && brandVariants.some((v) => v && t.includes(v));
      let bucket;
      if (isBrand || HIGH.test(t)) bucket = 'high';
      else if (MED.test(t)) bucket = 'medium';
      else bucket = 'low';
      const b = buckets[bucket];
      b.keywords.push(kw);
      b.spend += Number(kw.cost || 0);
      b.revenue += Number(kw.conversion_value || 0);
      b.orders += Number(kw.conversions || 0);
      b.clicks += Number(kw.clicks || 0);
      b.impressions += Number(kw.impressions || 0);
    });

    const enrich = (v) => ({
      ...v,
      roas: v.spend > 0 ? v.revenue / v.spend : 0,
      cr: v.clicks > 0 ? (v.orders / v.clicks) * 100 : 0,
      aov: v.orders > 0 ? v.revenue / v.orders : 0,
      count: v.keywords.length,
    });
    return {
      high: enrich(buckets.high),
      medium: enrich(buckets.medium),
      low: enrich(buckets.low),
    };
  }, [keywordsGrouped, brandName]);

  // ── Derived: Google campaign segmentation (Brand/Generic/PMAX/etc.) ──────
  const googleCampaignsBySegment = useMemo(() => {
    const segments = {
      brand:    { key: 'brand',    label: 'Brand Search',     rows: [], spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0 },
      generic:  { key: 'generic',  label: 'Generic Search',   rows: [], spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0 },
      pmax:     { key: 'pmax',     label: 'Performance Max',  rows: [], spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0 },
      shopping: { key: 'shopping', label: 'Shopping',         rows: [], spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0 },
      display:  { key: 'display',  label: 'Display',          rows: [], spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0 },
      video:    { key: 'video',    label: 'Video / YouTube',  rows: [], spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0 },
      other:    { key: 'other',    label: 'Diğer',            rows: [], spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0 },
    };
    const bn = (brandName || '').toLowerCase().trim();
    const brandVariants = bn
      ? [bn, bn.replace(/\s+/g, ''), bn.replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ç/g, 'c').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ı/g, 'i')]
      : [];
    if (bn.length > 4) brandVariants.push(bn.slice(0, 4));

    combinedCampaigns
      .filter((c) => c.platform === 'Google')
      .forEach((c) => {
        const type = (c.type || '').toUpperCase();
        const name = (c.name || '').toLowerCase();
        let key;
        if (type.includes('PERFORMANCE_MAX')) key = 'pmax';
        else if (type.includes('SHOPPING')) key = 'shopping';
        else if (type.includes('DISPLAY')) key = 'display';
        else if (type.includes('VIDEO')) key = 'video';
        else if (type.includes('SEARCH')) {
          const isBrand = (brandVariants.length > 0 && brandVariants.some((v) => v && name.includes(v)))
            || /(?:^|[_\s])brnd(?:[_\s+]|$)/.test(name) || /(?:^|[_\s])brand(?:[_\s]|$)/.test(name) || name.includes('searchbrand');
          key = isBrand ? 'brand' : 'generic';
        } else {
          key = 'other';
        }
        const seg = segments[key];
        seg.rows.push(c);
        seg.spend += c.spend;
        seg.revenue += c.revenue;
        seg.orders += c.orders;
        seg.clicks += c.clicks;
        seg.impressions += c.impressions;
      });

    const totalGoogleSpend = Object.values(segments).reduce((s, x) => s + x.spend, 0);
    return Object.values(segments)
      .filter((s) => s.rows.length > 0 && s.key !== 'other')
      .map((s) => ({
        ...s,
        roas: s.spend > 0 ? s.revenue / s.spend : 0,
        cr: s.clicks > 0 ? (s.orders / s.clicks) * 100 : 0,
        aov: s.orders > 0 ? s.revenue / s.orders : 0,
        spendShare: totalGoogleSpend > 0 ? (s.spend / totalGoogleSpend) * 100 : 0,
      }));
  }, [combinedCampaigns, brandName]);

  // ── Derived: Zero-sales keywords (clean-up table) ────────────────────────
  const zeroSalesKeywords = useMemo(() => {
    return keywordsGrouped
      .filter((kw) => Number(kw.cost || 0) > 0 && Number(kw.conversions || 0) === 0)
      .map((kw) => ({
        ...kw,
        cleanupAction: getKeywordCleanupAction({
          spend: kw.cost,
          clicks: kw.clicks,
          keywordText: kw.keyword_text,
          brandName,
        }),
      }))
      .sort((a, b) => Number(b.cost || 0) - Number(a.cost || 0))
      .slice(0, 50);
  }, [keywordsGrouped, brandName]);

  // ── Derived: Meta funnel split by campaign objective ─────────────────────
  const metaByObjective = useMemo(() => {
    const buckets = {
      conversion: { key: 'conversion', label: 'Conversion / Sales', color: 'emerald', spend: 0, revenue: 0, orders: 0, clicks: 0 },
      traffic:    { key: 'traffic',    label: 'Traffic',            color: 'blue',    spend: 0, revenue: 0, orders: 0, clicks: 0 },
      awareness:  { key: 'awareness',  label: 'Awareness / Reach',  color: 'violet',  spend: 0, revenue: 0, orders: 0, clicks: 0 },
      engagement: { key: 'engagement', label: 'Engagement / Video', color: 'indigo',  spend: 0, revenue: 0, orders: 0, clicks: 0 },
      other:      { key: 'other',      label: 'Diğer',              color: 'amber',   spend: 0, revenue: 0, orders: 0, clicks: 0 },
    };
    const mRaw = Array.isArray(metaCampaigns) ? metaCampaigns : (metaCampaigns?.rawData || []);
    mRaw.forEach((r) => {
      const obj = String(r.campaignObjective || r.campaign_objective || '').toUpperCase();
      let key = 'other';
      if (obj.includes('CONVERSION') || obj.includes('SALES') || obj.includes('CATALOG') || obj.includes('PURCHASE')) key = 'conversion';
      else if (obj.includes('TRAFFIC') || obj.includes('LINK_CLICKS')) key = 'traffic';
      else if (obj.includes('AWARENESS') || obj.includes('REACH') || obj.includes('BRAND')) key = 'awareness';
      else if (obj.includes('ENGAGEMENT') || obj.includes('VIDEO_VIEWS') || obj.includes('VIDEO')) key = 'engagement';
      const b = buckets[key];
      b.spend += Number(r.spend || 0);
      b.revenue += Number(r.purchaseValue || r.purchase_value || 0);
      b.orders += Number(r.purchaseConversions || r.purchase_conversions || 0);
      b.clicks += Number(r.clicks || 0);
    });
    return Object.values(buckets)
      .filter((b) => b.spend > 0 || b.orders > 0)
      .map((b) => ({
        ...b,
        roas: b.spend > 0 ? b.revenue / b.spend : 0,
        cr: b.clicks > 0 ? (b.orders / b.clicks) * 100 : 0,
      }));
  }, [metaCampaigns]);

  // ── Derived: Campaign name → objective lookup (for creative cards M7) ──
  const campaignObjectiveMap = useMemo(() => {
    const map = {};
    const mRaw = Array.isArray(metaCampaigns) ? metaCampaigns : (metaCampaigns?.rawData || []);
    mRaw.forEach((r) => {
      const name = (r.campaignName || r.campaign_name || '').trim();
      if (name) {
        const obj = String(r.campaignObjective || r.campaign_objective || '').toUpperCase();
        if (obj.includes('CONVERSION') || obj.includes('SALES') || obj.includes('CATALOG') || obj.includes('PURCHASE')) map[name] = 'Sales';
        else if (obj.includes('TRAFFIC') || obj.includes('LINK_CLICKS')) map[name] = 'Traffic';
        else if (obj.includes('AWARENESS') || obj.includes('REACH') || obj.includes('BRAND')) map[name] = 'Awareness';
        else if (obj.includes('ENGAGEMENT') || obj.includes('VIDEO_VIEWS') || obj.includes('VIDEO')) map[name] = 'Engagement';
        else if (obj) map[name] = obj.slice(0, 20);
      }
    });
    return map;
  }, [metaCampaigns]);

  // ── Derived: Budget allocation actual vs target ──────────────────────────
  // Target mix (Ali Bey önerisi): Brand+PMAX 60-65%, Generic ~15%, Meta 20-25%
  const budgetAllocation = useMemo(() => {
    const segMap = Object.fromEntries(googleCampaignsBySegment.map((s) => [s.key, s]));
    const brandSpend = segMap.brand?.spend || 0;
    const pmaxSpend = segMap.pmax?.spend || 0;
    const genericSpend = segMap.generic?.spend || 0;
    const otherGoogleSpend = (segMap.shopping?.spend || 0) + (segMap.display?.spend || 0) + (segMap.video?.spend || 0) + (segMap.other?.spend || 0);
    const metaSpend = metaSummary.spend || 0;
    const total = brandSpend + pmaxSpend + genericSpend + otherGoogleSpend + metaSpend;
    if (total === 0) return null;
    const brandPmaxShare = ((brandSpend + pmaxSpend) / total) * 100;
    const genericShare = (genericSpend / total) * 100;
    const metaShare = (metaSpend / total) * 100;
    const otherShare = (otherGoogleSpend / total) * 100;
    const targets = { brandPmax: 62.5, generic: 15, meta: 22.5 };
    const classify = (actual, target) => {
      const dev = Math.abs(actual - target);
      if (dev <= 5) return 'green';
      if (dev <= 15) return 'amber';
      return 'red';
    };
    const recommendation = (actual, target, label) => {
      const dev = actual - target;
      if (Math.abs(dev) <= 5) return `${label} hedef bandında, koru.`;
      if (dev > 5) return `${label} hedef üstünde (%${actual.toFixed(1)} vs %${target}) — ${dev > 15 ? 'belirgin azalt' : 'hafif azalt'}.`;
      return `${label} hedef altında (%${actual.toFixed(1)} vs %${target}) — ${Math.abs(dev) > 15 ? 'belirgin artır' : 'hafif artır'}.`;
    };
    return {
      total,
      actual: { brandPmax: brandPmaxShare, generic: genericShare, meta: metaShare, other: otherShare },
      target: targets,
      status: {
        brandPmax: classify(brandPmaxShare, targets.brandPmax),
        generic: classify(genericShare, targets.generic),
        meta: classify(metaShare, targets.meta),
      },
      recommendations: {
        brandPmax: recommendation(brandPmaxShare, targets.brandPmax, 'Brand+PMAX'),
        generic: recommendation(genericShare, targets.generic, 'Generic Search'),
        meta: recommendation(metaShare, targets.meta, 'Meta Ads'),
      },
    };
  }, [googleCampaignsBySegment, metaSummary]);

  // ── Derived: Items grouped by category ────────────────────────────────────
  const itemsByCategory = useMemo(() => {
    const map = new Map();
    itemSummary.forEach((r) => {
      const cat = r.itemCategory || r.item_category || 'Diğer';
      const existing = map.get(cat);
      const revenue = Number(r.totalRevenue || r.total_revenue || 0);
      const purchased = Number(r.totalItemsPurchased || r.total_items_purchased || 0);
      const viewed = Number(r.totalItemsViewed || r.total_items_viewed || 0);
      if (existing) {
        existing.revenue += revenue;
        existing.purchased += purchased;
        existing.viewed += viewed;
        existing.itemCount += 1;
      } else {
        map.set(cat, { category: cat, revenue, purchased, viewed, itemCount: 1 });
      }
    });
    return Array.from(map.values()).map((r) => ({
      ...r,
      cr: r.viewed > 0 ? (r.purchased / r.viewed) * 100 : 0,
      aov: r.purchased > 0 ? r.revenue / r.purchased : 0,
    }));
  }, [itemSummary]);

  // ── Derived: Items grouped by product line (iPhone / MacBook / iPad / Watch) ─
  const itemsByProductLine = useMemo(() => {
    // Aksesuar ve küçük ürünleri hariç tutmak için exclude pattern
    const excludePatterns = /aksesuar|case|kılıf|kapak|kordon|kayış|şarj|adaptör|kablo|airpod|airtag|pencil|keyboard|magic mouse|magic trackpad|homepod|apple tv|beats/i;
    const lines = {
      iPhone: { pattern: /iphone/i, items: [] },
      MacBook: { pattern: /macbook|mac\s?book/i, items: [] },
      iPad: { pattern: /ipad/i, items: [] },
      'Apple Watch': { pattern: /apple\s?watch|watch\s?(se|ultra|series)/i, items: [] },
      Mac: { pattern: /\bmac\b|imac|mac\s?(mini|studio|pro\b(?!.*book))/i, items: [] },
    };

    itemSummary.forEach((r) => {
      const name = (r.itemName || r.item_name || '').toLowerCase();
      if (excludePatterns.test(name)) return;
      for (const [, line] of Object.entries(lines)) {
        if (line.pattern.test(name)) {
          line.items.push(r);
          return;
        }
      }
    });

    return Object.entries(lines)
      .map(([lineName, { items }]) => {
        const revenue = items.reduce((s, r) => s + Number(r.totalRevenue || r.total_revenue || 0), 0);
        const purchased = items.reduce((s, r) => s + Number(r.totalItemsPurchased || r.total_items_purchased || 0), 0);
        const viewed = items.reduce((s, r) => s + Number(r.totalItemsViewed || r.total_items_viewed || 0), 0);
        // Top 5 SKU by revenue
        const topSkus = [...items]
          .sort((a, b) => Number(b.totalRevenue || b.total_revenue || 0) - Number(a.totalRevenue || a.total_revenue || 0))
          .slice(0, 5)
          .map((r) => ({
            name: r.itemName || r.item_name || '-',
            revenue: Number(r.totalRevenue || r.total_revenue || 0),
            purchased: Number(r.totalItemsPurchased || r.total_items_purchased || 0),
          }));
        return {
          productLine: lineName,
          revenue,
          purchased,
          viewed,
          skuCount: items.length,
          cr: viewed > 0 ? (purchased / viewed) * 100 : 0,
          aov: purchased > 0 ? revenue / purchased : 0,
          topSkus,
        };
      })
      .filter((r) => r.skuCount > 0);
  }, [itemSummary]);

  // brandVsGenericChart kaldırıldı — artık keyword-bazlı card layout kullanılıyor

  // ── Column definitions ────────────────────────────────────────────────────

  const campaignColumns = [
    { key: 'name', label: 'Kampanya Adı', sortable: true },
    {
      key: 'platform',
      label: 'Platform',
      sortable: true,
      render: (v) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            v === 'Google'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-indigo-100 text-indigo-700'
          }`}
        >
          {v}
        </span>
      ),
    },
    { key: 'type', label: 'Tür', sortable: true },
    {
      key: 'spend',
      label: 'Harcama',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v),
    },
    {
      key: 'revenue',
      label: 'Ciro (GA4)',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v),
    },
    {
      key: 'roas',
      label: 'ROAS (GA4)',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'roas'),
    },
    {
      key: 'cr',
      label: 'CR (%)',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'percent'),
    },
    {
      key: 'aov',
      label: 'AOV',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v),
    },
    {
      key: 'orders',
      label: 'Sipariş (GA4)',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'number'),
    },
    {
      key: 'flag',
      label: 'Durum',
      sortable: false,
      accessor: (row) => row.flag?.label || '',
      render: (_v, row) => <FlagBadge flag={row.flag} />,
    },
  ];

  const keywordColumns = [
    { key: 'keyword_text', label: 'Keyword', sortable: true },
    { key: 'campaign_name', label: 'Kampanya', sortable: true },
    { key: 'ad_group_name', label: 'Ad Group', sortable: true },
    {
      key: 'cost',
      label: 'Harcama',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v),
    },
    {
      key: 'conversion_value',
      label: 'Ciro (Google Ads)',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v),
    },
    {
      key: 'roas',
      label: 'ROAS (Google Ads)',
      numeric: true,
      sortable: true,
      accessor: (row) =>
        row.cost > 0 ? Number(row.conversion_value || 0) / Number(row.cost) : 0,
      render: (v) => fmt(v, 'roas'),
    },
    {
      key: 'conversions',
      label: 'Dönüşüm (Google Ads)',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'number'),
    },
    {
      key: 'impressions',
      label: 'Gösterim',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'number'),
    },
    {
      key: 'clicks',
      label: 'Tıklama',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'number'),
    },
    {
      key: 'flag',
      label: 'Durum',
      sortable: false,
      accessor: (row) => {
        const flag = getRowFlag(row.cost, row.conversion_value, row.conversions);
        return flag?.label || '';
      },
      render: (_v, row) => <FlagBadge flag={getRowFlag(row.cost, row.conversion_value, row.conversions)} />,
    },
  ];

  // Item columns - grouped by category
  const itemColumns = [
    { key: 'category', label: 'Kategori', sortable: true },
    {
      key: 'itemCount',
      label: 'Ürün Sayısı',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'number'),
    },
    {
      key: 'revenue',
      label: 'Ciro',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v),
    },
    {
      key: 'purchased',
      label: 'Satış Adedi',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'number'),
    },
    {
      key: 'aov',
      label: 'AOV',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v),
    },
    {
      key: 'cr',
      label: 'Görüntüleme→Satış (%)',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'percent'),
    },
    {
      key: 'viewed',
      label: 'Görüntülenme',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'number'),
    },
  ];

  // Top items - individual product detail
  const topItemColumns = [
    { key: 'itemName', label: 'Ürün', sortable: true, accessor: (r) => r.itemName || r.item_name || '-' },
    { key: 'itemCategory', label: 'Kategori', sortable: true, accessor: (r) => r.itemCategory || r.item_category || '-' },
    {
      key: 'totalRevenue',
      label: 'Ciro',
      numeric: true,
      sortable: true,
      accessor: (r) => Number(r.totalRevenue || r.total_revenue || 0),
      render: (v) => fmt(v),
    },
    {
      key: 'totalItemsPurchased',
      label: 'Satış',
      numeric: true,
      sortable: true,
      accessor: (r) => Number(r.totalItemsPurchased || r.total_items_purchased || 0),
      render: (v) => fmt(v, 'number'),
    },
    {
      key: 'conversionRate',
      label: 'CR (%)',
      numeric: true,
      sortable: true,
      accessor: (r) => Number(r.conversionRate || 0) * 100,
      render: (v) => fmt(v, 'percent'),
    },
  ];

  const productLineColumns = [
    { key: 'productLine', label: 'Ürün Ailesi', sortable: true },
    {
      key: 'revenue',
      label: 'Ciro',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v),
    },
    {
      key: 'purchased',
      label: 'Satış',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'number'),
    },
    {
      key: 'aov',
      label: 'AOV',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v),
    },
    {
      key: 'cr',
      label: 'View→Satış (%)',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'percent'),
    },
    {
      key: 'skuCount',
      label: 'SKU',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'number'),
    },
    {
      key: 'viewed',
      label: 'Görüntülenme',
      numeric: true,
      sortable: true,
      render: (v) => fmt(v, 'number'),
    },
  ];

  // ── Brand vs Generic AI deep analysis ───────────────────────────────────────
  // Returns the produced analysis text (so the email path can use it directly
  // without waiting for state updates).
  const generateBvgAnalysis = useCallback(async () => {
    if (!brandVsGenericKeywords) return '';
    setBvgAnalysisLoading(true);
    setBvgAnalysis('');

    const fmtN = (v) => (Number(v) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });
    const b = brandVsGenericKeywords.brand;
    const g = brandVsGenericKeywords.generic;

    const bvgData = `
Marka: ${brandName}
Dönem: ${activeDateLabel}
Toplam keyword sayısı: ${brandVsGenericKeywords.totalKeywords}

BRAND KEYWORDS (${b.count} adet):
- Harcama: ₺${fmtN(b.spend)} (toplam harcamanın %${fmtN(b.spendShare)}'i)
- Ciro: ₺${fmtN(b.revenue)} (toplam cironun %${fmtN(b.revenueShare)}'i)
- ROAS: ${fmtN(b.roas)}x
- Sipariş: ${fmtN(b.orders)}
- CR: %${fmtN(b.cr)}
- CTR: %${fmtN(b.ctr)}
- AOV: ₺${fmtN(b.aov)}
- Örnek brand keywordler: ${b.keywords.slice(0, 5).map((k) => k.keyword_text).join(', ')}

GENERIC KEYWORDS (${g.count} adet):
- Harcama: ₺${fmtN(g.spend)} (toplam harcamanın %${fmtN(g.spendShare)}'i)
- Ciro: ₺${fmtN(g.revenue)} (toplam cironun %${fmtN(g.revenueShare)}'i)
- ROAS: ${fmtN(g.roas)}x
- Sipariş: ${fmtN(g.orders)}
- CR: %${fmtN(g.cr)}
- CTR: %${fmtN(g.ctr)}
- AOV: ₺${fmtN(g.aov)}
- Örnek generic keywordler: ${g.keywords.slice(0, 5).map((k) => k.keyword_text).join(', ')}

Meta Ads harcaması: ₺${fmtN(metaSummary.spend)}
Meta Ads cirosu (last-click): ₺${fmtN(metaSummary.revenue)}
GA4 toplam site cirosu: ₺${fmtN(ga4Summary.revenue)}
`.trim();

    try {
      const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          model: 'gemini-2.5-flash',
          temperature: 0.3,
          maxOutputTokens: 3072,
          messages: [
            {
              role: 'user',
              content: `Sen kıdemli bir dijital pazarlama stratejistisin. Telefon ve elektronik sektöründe faaliyet gösteren bir e-ticaret markasının Brand vs Generic keyword analizini yapıyorsun.

Sektör dinamikleri (bunları analizinde mutlaka kullan):
- Telefon/elektronik sektöründe satın alma süreci 4-7 güne yayılır. Müşteri önce generic aramalar yapar (ör. "iphone 15 fiyat"), sonra Meta/Instagram reklamlarını görür, ardından karar aşamasında brand keyword ile arar (ör. "${brandName} iphone") ve satın alır.
- GA4 last-click attribution modelinde brand keywordler doğal olarak yüksek ROAS gösterir çünkü satın alma yolculuğunun son temas noktasıdır.
- Generic keywordlerin düşük last-click ROAS'ı, onların değersiz olduğu anlamına gelmez — üst funnel'da talep yaratma ve awareness rolü vardır.
- Meta Ads aynı şekilde last-click'te düşük görünür çünkü consideration aşamasında katkı sağlar, son tıklama Google brand search'e gider.
- Add to Cart conversion'ı bazı kampanyalarda bilinçli olarak micro conversion hedefi olarak kullanılıyoruz — bu bir hata değil, üst funnel kampanyalarında purchase yerine ATC optimizasyonu yapılır çünkü purchase event'i yeterli sinyal üretmez.

Aşağıdaki gerçek verileri kullanarak Türkçe kısa ve öz bir analiz yaz. Başlıkları bold/yıldız yapmadan düz yaz:

1. Mevcut Durum;
(Brand vs Generic dağılımını, ROAS farkını ve attribution model etkisini 3-4 maddede özetle.)

2. Brand Kesilirse Ne Olur;
(Meta awareness → generic search → brand search yolculuğunu açıkla. Brand kesilirse tahmini etki. 2-3 madde.)

Kurallar:
- Toplamda en fazla 7 madde olsun, kısa ve net yaz.
- Rakamları mutlaka kullan.
- Profesyonel ama okunması kolay bir dil kullan.
- Aksiyon/öneri yazma — sadece mevcut durumu ve gözlemleri yorumla. "Şunu yap", "Bütçeyi artır", "Negative ekle" gibi öneriler yasak.

VERİLER:
${bvgData}`
            }
          ]
        })
      });

      if (!resp.ok) throw new Error('AI yanıt veremedi');
      const data = await resp.json();
      const text = data?.message || data?.text || data?.answer || 'Yanıt alınamadı.';
      setBvgAnalysis(text);
      return text;
    } catch {
      const text = 'Analiz oluşturulamadı. Lütfen tekrar deneyin.';
      setBvgAnalysis(text);
      return text;
    } finally {
      setBvgAnalysisLoading(false);
    }
  }, [brandVsGenericKeywords, brandName, activeDateLabel, metaSummary, ga4Summary, brandId]);

  // ── Product Line Growth AI analysis ──────────────────────────────────────────
  // Returns the produced analysis text (so the email path can use it directly).
  const generatePlAnalysis = useCallback(async () => {
    if (!itemsByProductLine.length) return '';
    setPlAnalysisLoading(true);
    setPlAnalysis('');

    const fmtN = (v) => (Number(v) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });

    // Her ürün ailesi için keyword bilgisini de ekle
    const keywordsByLine = {};
    if (keywordsGrouped.length) {
      const linePatterns = {
        iPhone: /iphone/i,
        MacBook: /macbook|mac\s?book/i,
        iPad: /ipad/i,
        'Apple Watch': /watch/i,
        Mac: /\bmac\b|imac/i,
      };
      keywordsGrouped.forEach((kw) => {
        const kwText = (kw.keyword_text || '').toLowerCase();
        for (const [line, pat] of Object.entries(linePatterns)) {
          if (pat.test(kwText)) {
            if (!keywordsByLine[line]) keywordsByLine[line] = { spend: 0, revenue: 0, impressions: 0, clicks: 0 };
            keywordsByLine[line].spend += Number(kw.cost || 0);
            keywordsByLine[line].revenue += Number(kw.conversion_value || 0);
            keywordsByLine[line].impressions += Number(kw.impressions || 0);
            keywordsByLine[line].clicks += Number(kw.clicks || 0);
            break;
          }
        }
      });
    }

    const totalRevenue = itemsByProductLine.reduce((s, r) => s + r.revenue, 0);

    const plData = itemsByProductLine.map((pl) => {
      const kwInfo = keywordsByLine[pl.productLine];
      const share = totalRevenue > 0 ? ((pl.revenue / totalRevenue) * 100).toFixed(1) : '0';
      let kwLine = '';
      if (kwInfo) {
        kwLine = `\n  Keyword harcama: ₺${fmtN(kwInfo.spend)} | Keyword ciro: ₺${fmtN(kwInfo.revenue)} | Gösterim: ${fmtN(kwInfo.impressions)} | Tıklama: ${fmtN(kwInfo.clicks)}`;
      }
      const topSkuLine = pl.topSkus?.length
        ? `\n  Top SKUlar: ${pl.topSkus.map((s) => `${s.name} (₺${fmtN(s.revenue)}, ${s.purchased} adet)`).join(' | ')}`
        : '';
      return `${pl.productLine}:
  Ciro: ₺${fmtN(pl.revenue)} (pay: %${share}) | Satış: ${pl.purchased} adet | AOV: ₺${fmtN(pl.aov)} | CR: %${fmtN(pl.cr)}${kwLine}${topSkuLine}`;
    }).join('\n\n');

    try {
      const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          model: 'gemini-2.5-flash',
          temperature: 0.3,
          maxOutputTokens: 4096,
          messages: [
            {
              role: 'user',
              content: `Sen Apple yetkili bayisinde çalışan kıdemli bir dijital pazarlama stratejistisin. Ürün ailesi bazında performans verisini analiz edip kârlı büyüme stratejisi çıkaracaksın.

Sektör bilgisi:
- Bu bir Apple Premium Reseller (yetkili bayi). iPhone satışları doğal olarak yüksek hacimli ama marjlar düşük.
- Mac, iPad ve Watch tarafında marjlar daha iyi, ama satış hacmi düşük. Hedef bu tarafı büyütmek.
- Rekabet: iPhone'da marketplace (Trendyol, Hepsiburada) ağırlıklı fiyat savaşı var. Mac/iPad'de daha az rakip, fiyat hassasiyeti daha düşük.
- Yeni model çıkışlarında (ör. iPhone 17, MacBook Air M5) arama hacmi spike yapıyor.

Aşağıdaki gerçek verileri kullanarak her ürün ailesi için kısa ve öz analiz yaz. Başlıkları bold/yıldız yapmadan düz yaz:

1. Mevcut Durum;
(Her ürün ailesinin ciro payı, satış hacmi, AOV karşılaştırması. iPhone'un driver olduğunu teyit et, diğerlerinin durumunu özetle.)

2. Öne Çıkanlar;
(Dikkat çeken metrikler — hangi ailede CR yüksek/düşük, AOV farkları, SKU yoğunluğu vs hacim ilişkisi.)

Kurallar:
- Toplamda en fazla 7 madde, kısa ve net.
- Rakamları mutlaka kullan, soyut konuşma.
- Profesyonel ama okunması kolay.
- Aksiyon/öneri/strateji yazma — sadece mevcut durumu ve gözlemleri yorumla. "Bütçe artır", "Kampanya kur", "Negative ekle" gibi öneriler yasak.

VERİLER:
Dönem: ${activeDateLabel}
Toplam ciro: ₺${fmtN(totalRevenue)}
GA4 site cirosu: ₺${fmtN(ga4Summary.revenue)}

${plData}`
            }
          ]
        })
      });

      if (!resp.ok) throw new Error('AI yanıt veremedi');
      const data = await resp.json();
      const text = data?.message || data?.text || data?.answer || 'Yanıt alınamadı.';
      setPlAnalysis(text);
      return text;
    } catch {
      const text = 'Analiz oluşturulamadı. Lütfen tekrar deneyin.';
      setPlAnalysis(text);
      return text;
    } finally {
      setPlAnalysisLoading(false);
    }
  }, [itemsByProductLine, keywordsGrouped, activeDateLabel, ga4Summary, brandId]);

  // ── Gemini helper — tek seferlik AI çağrısı ─────────────────────────────────
  const callGemini = useCallback(async (prompt, maxTokens = 3072) => {
    try {
      const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          model: 'gemini-2.5-flash',
          temperature: 0.3,
          maxOutputTokens: maxTokens,
          messages: [{ role: 'user', content: prompt }],
        }),
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      return data?.message || data?.text || data?.answer || null;
    } catch { return null; }
  }, [brandId]);

  // `generateAiSummary` is declared further down in this component (after
  // sendReportEmail), so we can't reference it directly in this callback's
  // closure or deps without hitting a TDZ during render. Stash the latest
  // function in a ref and call it via the ref instead.
  const generateAiSummaryRef = useRef(null);

  // ── Send Report Email ──────────────────────────────────────────────────────
  // Modes: 'email' (POST + send), 'export' (HTML + CSV download), 'csv' (CSV-only download)
  const sendReportEmail = useCallback(async (mode = 'email') => {
    if (emailSending) return;
    if (mode === 'email' && !emailRecipient.trim()) return;
    setEmailSending(true);
    setEmailResult(null);

    // Filename date suffix — prefer ISO yyyy-mm-dd_yyyy-mm-dd when start/end available
    const dateSuffix = (activeStartDate && activeEndDate)
      ? `${activeStartDate}_${activeEndDate}`
      : (activeDateLabel || 'rapor').replace(/[^a-zA-Z0-9]/g, '-');

    const fmtN = (v) => (Number(v) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });

    // ── Auto-generate any AI commentary the user hasn't already produced ──
    // The modal promises "AI yorumları dahil". We honour that by triggering
    // the page's three AI generators in parallel for whichever blocks are
    // still empty. Each generator returns its produced text directly so we
    // don't have to wait for the React state round-trip.
    const [aiSummaryText, bvgAnalysisText, plAnalysisText] = await Promise.all([
      aiSummary && aiSummary.trim()
        ? Promise.resolve(aiSummary)
        : (generateAiSummaryRef.current ? generateAiSummaryRef.current() : Promise.resolve('')),
      bvgAnalysis && bvgAnalysis.trim()
        ? Promise.resolve(bvgAnalysis)
        : (brandVsGenericKeywords ? generateBvgAnalysis() : Promise.resolve('')),
      plAnalysis && plAnalysis.trim()
        ? Promise.resolve(plAnalysis)
        : (itemsByProductLine.length > 0 ? generatePlAnalysis() : Promise.resolve('')),
    ]);

    // ── Build sections array for the email ───────────────────────────
    // NOTE: Static cards/tables + any AI commentary the user has generated
    // on the page (executive summary, brand-vs-generic, product-line). AI
    // blocks are only included when their text is already present in state —
    // we never trigger a fresh AI call from the email path.
    const sections = [];

    // 0) GlowyAI Executive Summary
    if (aiSummaryText && aiSummaryText.trim()) {
      sections.push({
        type: 'text',
        title: 'GlowyAI — Yönetici Özeti',
        text: aiSummaryText,
      });
    }

    // 1) GA4 Summary cards
    sections.push({
      type: 'cards',
      title: 'GA4 — Site Geneli',
      cards: [
        { label: 'Toplam Ciro', value: fmt(ga4Summary.revenue), delta: pct(ga4Summary.revenue, compareGa4Summary.revenue) },
        { label: 'Sipariş', value: fmt(ga4Summary.transactions, 'number'), delta: pct(ga4Summary.transactions, compareGa4Summary.transactions) },
        { label: 'Oturum', value: fmt(ga4Summary.sessions, 'number'), delta: pct(ga4Summary.sessions, compareGa4Summary.sessions) },
        { label: 'Kullanıcı', value: fmt(ga4Summary.users, 'number'), delta: pct(ga4Summary.users, compareGa4Summary.users) },
        { label: 'CR (%)', value: fmt(ga4Summary.cr, 'percent'), delta: pct(ga4Summary.cr, compareGa4Summary.cr) },
        { label: 'AOV', value: fmt(ga4Summary.aov), delta: pct(ga4Summary.aov, compareGa4Summary.aov) },
      ],
    });

    // 2) Google Ads cards
    sections.push({
      type: 'cards',
      title: 'Google Ads',
      cards: [
        { label: 'Harcama', value: fmt(googleSummary.spend), delta: pct(googleSummary.spend, compareGoogleSummary.spend) },
        { label: 'Ciro', value: fmt(googleSummary.revenue), delta: pct(googleSummary.revenue, compareGoogleSummary.revenue) },
        { label: 'ROAS', value: fmt(googleSummary.roas, 'roas'), delta: pct(googleSummary.roas, compareGoogleSummary.roas) },
        { label: 'Sipariş', value: fmt(googleSummary.orders, 'number'), delta: pct(googleSummary.orders, compareGoogleSummary.orders) },
        { label: 'CR (%)', value: fmt(googleSummary.cr, 'percent'), delta: pct(googleSummary.cr, compareGoogleSummary.cr) },
        { label: 'AOV', value: fmt(googleSummary.aov), delta: pct(googleSummary.aov, compareGoogleSummary.aov) },
      ],
    });

    // 2b) Brand Health Card (M9)
    {
      const brandSeg = googleCampaignsBySegment.find((s) => s.key === 'brand');
      if (brandSeg) {
        const brandCpc = brandSeg.clicks > 0 ? brandSeg.spend / brandSeg.clicks : 0;
        sections.push({
          type: 'cards',
          title: 'Brand Sağlık Kartı (Brand Search)',
          cards: [
            { label: 'Brand Harcama', value: fmt(brandSeg.spend) },
            { label: 'Brand CPC', value: fmt(brandCpc) },
            { label: 'Brand CR (%)', value: fmt(brandSeg.cr, 'percent') },
            { label: 'Brand AOV', value: fmt(brandSeg.aov) },
            { label: 'Brand ROAS', value: fmt(brandSeg.roas, 'roas') },
          ],
        });
      }
    }

    // 3) Meta Ads cards
    sections.push({
      type: 'cards',
      title: 'Meta Ads',
      cards: [
        { label: 'Harcama', value: fmt(metaSummary.spend), delta: pct(metaSummary.spend, compareMetaSummary.spend) },
        { label: 'Ciro', value: fmt(metaSummary.revenue), delta: pct(metaSummary.revenue, compareMetaSummary.revenue) },
        { label: 'ROAS', value: fmt(metaSummary.roas, 'roas'), delta: pct(metaSummary.roas, compareMetaSummary.roas) },
        { label: 'Sipariş', value: fmt(metaSummary.orders, 'number'), delta: pct(metaSummary.orders, compareMetaSummary.orders) },
        { label: 'CR (%)', value: fmt(metaSummary.cr, 'percent'), delta: pct(metaSummary.cr, compareMetaSummary.cr) },
        { label: 'AOV', value: fmt(metaSummary.aov), delta: pct(metaSummary.aov, compareMetaSummary.aov) },
      ],
    });

    // 5) Campaign table (top 15 — trimmed to avoid Gmail clipping)
    const topCampaigns = [...combinedCampaigns].sort((a, b) => b.spend - a.spend).slice(0, 15);
    if (topCampaigns.length > 0) {
      sections.push({
        type: 'table',
        title: 'Kampanya Performansı (Top 15)',
        columns: ['Kampanya', 'Platform', 'Harcama', 'Ciro', 'ROAS', 'Sipariş'],
        rows: topCampaigns.map((c) => [c.name, c.platform, fmt(c.spend), fmt(c.revenue), fmt(c.roas, 'roas'), fmt(c.orders, 'number')]),
      });
    }

    // 5a) Google Campaigns by Segment (Brand/Generic/PMAX/Shopping/...)
    if (googleCampaignsBySegment.length > 0) {
      sections.push({
        type: 'table',
        title: 'Google Ads — Segment Bazında Özet (GA4 Attribution)',
        subtitle: `${googleCampaignsBySegment.length} segment · ${googleCampaignsBySegment.reduce((s, x) => s + x.rows.length, 0)} kampanya`,
        columns: ['Segment', 'Kampanya Sayısı', '% Pay', 'Harcama', 'Ciro', 'ROAS', 'Sipariş'],
        rows: googleCampaignsBySegment.map((seg) => [
          seg.label,
          String(seg.rows.length),
          `%${fmtN(seg.spendShare)}`,
          fmt(seg.spend),
          fmt(seg.revenue),
          fmt(seg.roas, 'roas'),
          fmt(seg.orders, 'number'),
        ]),
      });
    }


    // 5c) Sıfır Ciro Keyword Temizlik tablosu (top 20 — full list in CSV attachment)
    if (zeroSalesKeywords.length > 0) {
      const zeroTop = zeroSalesKeywords.slice(0, 20);
      const zeroTotalSpend = zeroSalesKeywords.reduce((s, k) => s + Number(k.cost || 0), 0);
      sections.push({
        type: 'table',
        title: 'Sıfır Ciro Keyword Listesi (Top 20)',
        subtitle: `${zeroSalesKeywords.length} keyword · Toplam boşa giden harcama: ${fmt(zeroTotalSpend)} · Tam liste ekteki CSV'de`,
        columns: ['Keyword', 'Kampanya', 'Harcama', 'Tıklama', 'Gösterim'],
        rows: zeroTop.map((kw) => [
          kw.keyword_text || '-',
          kw.campaign_name || '-',
          fmt(kw.cost),
          fmt(kw.clicks, 'number'),
          fmt(kw.impressions, 'number'),
        ]),
      });

    }

    // 6) Keyword Intent breakdown cards
    if (keywordsByIntent) {
      sections.push({
        type: 'cards',
        title: 'Keyword Intent Kırılımı',
        subtitle: 'High / Medium / Low — niyet bandına göre kırılım',
        cards: [
          { label: `Yüksek Niyet (${keywordsByIntent.high.count})`, value: `${fmt(keywordsByIntent.high.spend)} · ROAS ${fmt(keywordsByIntent.high.roas, 'roas')}` },
          { label: `Orta Niyet (${keywordsByIntent.medium.count})`,  value: `${fmt(keywordsByIntent.medium.spend)} · ROAS ${fmt(keywordsByIntent.medium.roas, 'roas')}` },
          { label: `Düşük Niyet (${keywordsByIntent.low.count})`,    value: `${fmt(keywordsByIntent.low.spend)} · ROAS ${fmt(keywordsByIntent.low.roas, 'roas')}` },
        ],
      });
    }

    // 6b) Top keywords table (top 15 — full list in CSV attachment)
    const topKw = [...keywordsGrouped].sort((a, b) => Number(b.cost || 0) - Number(a.cost || 0)).slice(0, 15);
    if (topKw.length > 0) {
      sections.push({
        type: 'table',
        title: 'Anahtar Kelime Performansı (Top 15)',
        subtitle: 'Tam liste ekteki CSV\'de',
        columns: ['Keyword', 'Kampanya', 'Harcama', 'Ciro', 'ROAS', 'Tıklama'],
        rows: topKw.map((kw) => [kw.keyword_text, kw.campaign_name, fmt(kw.cost), fmt(kw.conversion_value), fmt(kw.cost > 0 ? kw.conversion_value / kw.cost : 0, 'roas'), fmt(kw.clicks, 'number')]),
      });
    }


    // 7) Brand vs Generic summary (if available)
    if (brandVsGenericKeywords) {
      const b = brandVsGenericKeywords.brand;
      const g = brandVsGenericKeywords.generic;
      sections.push({
        type: 'cards',
        title: 'Brand vs Generic — Özet',
        subtitle: `${brandVsGenericKeywords.totalKeywords} keyword analiz edildi`,
        cards: [
          { label: 'Brand Harcama', value: `${fmt(b.spend)} (%${fmtN(b.spendShare)})` },
          { label: 'Brand Ciro', value: `${fmt(b.revenue)} (%${fmtN(b.revenueShare)})` },
          { label: 'Brand ROAS', value: fmt(b.roas, 'roas') },
          { label: 'Generic Harcama', value: `${fmt(g.spend)} (%${fmtN(g.spendShare)})` },
          { label: 'Generic Ciro', value: `${fmt(g.revenue)} (%${fmtN(g.revenueShare)})` },
          { label: 'Generic ROAS', value: fmt(g.roas, 'roas') },
        ],
      });
    }

    // 7b) Brand vs Generic — AI analizi
    if (bvgAnalysisText && bvgAnalysisText.trim()) {
      sections.push({
        type: 'text',
        title: 'Brand vs Generic — AI Analiz',
        text: bvgAnalysisText,
      });
    }


    // 8c) Meta Funnel cards (by campaign objective)
    if (metaByObjective && metaByObjective.length > 0) {
      sections.push({
        type: 'cards',
        title: 'Meta Funnel Kırılımı (Kampanya Hedefine Göre)',
        subtitle: 'Kaynak: meta_campaign_performance · Meta native attribution',
        cards: metaByObjective.map((b) => ({
          label: b.label,
          value: `${fmt(b.spend)} · ROAS ${b.spend > 0 ? fmt(b.roas, 'roas') : '-'}`,
        })),
      });
    }

    // 9) Product line table
    if (itemsByProductLine.length > 0) {
      sections.push({
        type: 'table',
        title: 'Ürün Ailesi Kırılımı',
        columns: ['Ürün Ailesi', 'Ciro', 'Satış', 'AOV', 'View→Satış %', 'SKU'],
        rows: itemsByProductLine.map((pl) => [pl.productLine, fmt(pl.revenue), fmt(pl.purchased, 'number'), fmt(pl.aov), fmt(pl.cr, 'percent'), String(pl.skuCount)]),
      });
    }

    // 9b) Ürün Ailesi — AI analizi
    if (plAnalysisText && plAnalysisText.trim()) {
      sections.push({
        type: 'text',
        title: 'Ürün Ailesi — AI Analiz',
        text: plAnalysisText,
      });
    }


    // 11) Top 20 products table (ciro bazlı sıralı) — kreatiflerin ÖNÜNDE (Gmail clipping)
    const top20Items = [...itemSummary]
      .sort((a, b) => Number(b.totalRevenue || b.total_revenue || 0) - Number(a.totalRevenue || a.total_revenue || 0))
      .slice(0, 20);
    if (top20Items.length > 0) {
      sections.push({
        type: 'table',
        title: 'En Çok Satan Ürünler (Top 20)',
        columns: ['Ürün', 'Kategori', 'Ciro', 'Satış', 'CR (%)'],
        rows: top20Items.map((r) => [
          r.itemName || r.item_name || '-',
          r.itemCategory || r.item_category || '-',
          fmt(Number(r.totalRevenue || r.total_revenue || 0)),
          fmt(Number(r.totalItemsPurchased || r.total_items_purchased || 0), 'number'),
          fmt(Number(r.conversionRate || 0) * 100, 'percent'),
        ]),
      });
    }

    // 12) Meta Creative Performance — top 10 (email boyutu için sınırlı)
    const topCreatives = [...creatives].sort((a, b) => b.purchases - a.purchases).slice(0, 10);
    if (topCreatives.length > 0) {
      sections.push({
        type: 'creatives',
        title: 'Kreatif Performansı — Meta (Top 10)',
        subtitle: `${creatives.length} kreatif · Satışa göre sıralı`,
        items: topCreatives.map((c) => {
          const det = creativeDetailsMap[c.creative_id];
          const name = det?.title || det?.name || c.ad_name || c.creative_id;
          const roas = c.spend > 0 ? c.revenue / c.spend : 0;
          return {
            creativeId: c.creative_id,
            adName: c.ad_name || '',
            name,
            body: det?.body || '',
            imageUrl: det?.enhanced_image_url || det?.best_media_url || det?.image_url || det?.thumbnail_url || '',
            thumbnailUrl: det?.thumbnail_url || det?.video_thumbnail_url || '',
            metrics: [
              { label: 'Harcama', value: fmt(c.spend) },
              { label: 'Ciro', value: fmt(c.revenue) },
              { label: 'ROAS', value: fmt(roas, 'roas') },
              { label: 'Satış', value: fmt(c.purchases, 'number') },
              { label: 'Tıklama', value: fmt(c.clicks, 'number') },
            ],
          };
        }),
      });
    }


    // CSV attachments
    const csvAttachments = [];

    // Campaign CSV
    if (combinedCampaigns.length > 0) {
      csvAttachments.push({
        filename: `kampanya-performansi_${dateSuffix}.csv`,
        content: generateCsvContent(combinedCampaigns, campaignColumns.map((c) => ({ label: c.label, accessor: c.accessor || c.key })), activeDateLabel),
      });
    }

    // Keyword CSV
    if (keywordsGrouped.length > 0) {
      csvAttachments.push({
        filename: `anahtar-kelime-performansi_${dateSuffix}.csv`,
        content: generateCsvContent(keywordsGrouped, keywordColumns.map((c) => ({ label: c.label, accessor: c.accessor || c.key })), activeDateLabel),
      });
    }

    // Brand vs Generic keyword CSV
    if (brandVsGenericKeywords && keywordsGrouped.length > 0) {
      const bn = brandName.toLowerCase().trim();
      const variants = [bn, bn.replace(/\s+/g, ''), bn.replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ç/g, 'c').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ı/g, 'i')];
      if (bn.length > 4) variants.push(bn.slice(0, 4));
      const bvgRows = keywordsGrouped.map((kw) => {
        const kwText = (kw.keyword_text || '').toLowerCase();
        const isBrand = variants.some((v) => kwText.includes(v));
        return {
          keyword_text: kw.keyword_text,
          tip: isBrand ? 'Brand' : 'Generic',
          campaign_name: kw.campaign_name,
          cost: Number(kw.cost || 0).toFixed(2),
          conversion_value: Number(kw.conversion_value || 0).toFixed(2),
          roas: kw.cost > 0 ? (kw.conversion_value / kw.cost).toFixed(2) : '0',
          conversions: kw.conversions,
          clicks: kw.clicks,
          impressions: kw.impressions,
        };
      });
      const bvgCols = [
        { label: 'Keyword', key: 'keyword_text' },
        { label: 'Tip', key: 'tip' },
        { label: 'Kampanya', key: 'campaign_name' },
        { label: 'Harcama', key: 'cost' },
        { label: 'Ciro', key: 'conversion_value' },
        { label: 'ROAS', key: 'roas' },
        { label: 'Sipariş', key: 'conversions' },
        { label: 'Tıklama', key: 'clicks' },
        { label: 'Gösterim', key: 'impressions' },
      ];
      csvAttachments.push({
        filename: `brand-vs-generic-keywords_${dateSuffix}.csv`,
        content: generateCsvContent(bvgRows, bvgCols, activeDateLabel),
      });
    }

    // Product line CSV
    if (itemsByProductLine.length > 0) {
      csvAttachments.push({
        filename: `urun-ailesi-kirilimi_${dateSuffix}.csv`,
        content: generateCsvContent(itemsByProductLine, productLineColumns.map((c) => ({ label: c.label, accessor: c.accessor || c.key })), activeDateLabel),
      });
    }

    // Meta Creative CSV
    if (creatives.length > 0) {
      const creativeCsvRows = [...creatives].sort((a, b) => b.purchases - a.purchases).map((c) => {
        const det = creativeDetailsMap[c.creative_id];
        const name = det?.title || det?.name || c.ad_name || c.creative_id;
        const roas = c.spend > 0 ? c.revenue / c.spend : 0;
        return {
          ad_name: name,
          creative_id: c.creative_id,
          spend: c.spend.toFixed(2),
          revenue: c.revenue.toFixed(2),
          roas: roas.toFixed(2),
          purchases: c.purchases,
          clicks: c.clicks,
          impressions: c.impressions,
        };
      });
      const creativeCols = [
        { label: 'Reklam Adı', key: 'ad_name' },
        { label: 'Creative ID', key: 'creative_id' },
        { label: 'Harcama', key: 'spend' },
        { label: 'Ciro', key: 'revenue' },
        { label: 'ROAS', key: 'roas' },
        { label: 'Satış', key: 'purchases' },
        { label: 'Tıklama', key: 'clicks' },
        { label: 'Gösterim', key: 'impressions' },
      ];
      csvAttachments.push({
        filename: `meta-kreatif-performansi_${dateSuffix}.csv`,
        content: generateCsvContent(creativeCsvRows, creativeCols, activeDateLabel),
      });
    }

    // Top 50 items CSV
    if (itemSummary.length > 0) {
      csvAttachments.push({
        filename: `en-cok-satan-urunler_${dateSuffix}.csv`,
        content: generateCsvContent(itemSummary.slice(0, 50), topItemColumns.map((c) => ({ label: c.label, accessor: c.accessor || c.key })), activeDateLabel),
      });
    }

    // ── New raw-data CSVs aligned with the refactored page ───────────────────

    // Google Ads — Segmented Campaigns (Brand / Generic / PMAX / Shopping / Display / Video / Diğer)
    if (googleCampaignsBySegment.length > 0) {
      const segRows = [];
      googleCampaignsBySegment.forEach((seg) => {
        seg.rows.forEach((c) => {
          segRows.push({
            segment: seg.label,
            kampanya: c.name || '-',
            kampanya_tipi: c.type || '-',
            harcama: Number(c.spend || 0).toFixed(2),
            ciro: Number(c.revenue || 0).toFixed(2),
            roas: c.spend > 0 ? (c.revenue / c.spend).toFixed(2) : '0',
            siparis: Number(c.orders || 0),
            tiklama: Number(c.clicks || 0),
            gosterim: Number(c.impressions || 0),
            cr_yuzde: c.clicks > 0 ? ((c.orders / c.clicks) * 100).toFixed(2) : '0',
            aov: c.orders > 0 ? (c.revenue / c.orders).toFixed(2) : '0',
          });
        });
      });
      const segCols = [
        { label: 'Segment', key: 'segment' },
        { label: 'Kampanya', key: 'kampanya' },
        { label: 'Kampanya Tipi', key: 'kampanya_tipi' },
        { label: 'Harcama', key: 'harcama' },
        { label: 'Ciro (GA4)', key: 'ciro' },
        { label: 'ROAS', key: 'roas' },
        { label: 'Sipariş (GA4)', key: 'siparis' },
        { label: 'Tıklama', key: 'tiklama' },
        { label: 'Gösterim', key: 'gosterim' },
        { label: 'CR (%)', key: 'cr_yuzde' },
        { label: 'AOV', key: 'aov' },
      ];
      csvAttachments.push({
        filename: `google-kampanya-segment-bazinda_${dateSuffix}.csv`,
        content: generateCsvContent(segRows, segCols, activeDateLabel),
      });
    }

    // Sıfır Ciro Keyword Cleanup CSV
    if (zeroSalesKeywords.length > 0) {
      const zsRows = zeroSalesKeywords.map((kw) => ({
        keyword: kw.keyword_text || '-',
        kampanya: kw.campaign_name || '-',
        harcama: Number(kw.cost || 0).toFixed(2),
        tiklama: Number(kw.clicks || 0),
        gosterim: Number(kw.impressions || 0),
        ctr_yuzde: kw.impressions > 0 ? ((kw.clicks / kw.impressions) * 100).toFixed(2) : '0',
        donusum: Number(kw.conversions || 0),
      }));
      const zsCols = [
        { label: 'Keyword', key: 'keyword' },
        { label: 'Kampanya', key: 'kampanya' },
        { label: 'Harcama', key: 'harcama' },
        { label: 'Tıklama', key: 'tiklama' },
        { label: 'Gösterim', key: 'gosterim' },
        { label: 'CTR (%)', key: 'ctr_yuzde' },
        { label: 'Dönüşüm', key: 'donusum' },
      ];
      csvAttachments.push({
        filename: `sifir-ciro-keyword-temizlik_${dateSuffix}.csv`,
        content: generateCsvContent(zsRows, zsCols, activeDateLabel),
      });
    }

    // Keywords by Intent CSV (high / medium / low) — full keyword list with intent label
    if (keywordsByIntent && keywordsGrouped.length > 0) {
      const HIGH = /(satin\s?al|sipari|fiyat|ucuz|indirim|kampanya|nere|magaza|stok|alinir|al\b)/i;
      const MED = /(iphone|macbook|ipad|airpod|watch|imac|mini|pro|max|plus|ultra|magic|airtag|pencil)/i;
      const bn = (brandName || '').toLowerCase().trim();
      const variants = bn
        ? [bn, bn.replace(/\s+/g, ''), bn.replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ç/g, 'c').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ı/g, 'i')]
        : [];
      if (bn.length > 4) variants.push(bn.slice(0, 4));
      const intentRows = keywordsGrouped.map((kw) => {
        const t = (kw.keyword_text || '').toLowerCase();
        const isBrand = variants.some((v) => v && t.includes(v));
        let intent;
        if (isBrand || HIGH.test(t)) intent = 'High';
        else if (MED.test(t)) intent = 'Medium';
        else intent = 'Low';
        const cost = Number(kw.cost || 0);
        const rev = Number(kw.conversion_value || 0);
        return {
          keyword: kw.keyword_text || '-',
          intent,
          kampanya: kw.campaign_name || '-',
          harcama: cost.toFixed(2),
          ciro: rev.toFixed(2),
          roas: cost > 0 ? (rev / cost).toFixed(2) : '0',
          donusum: Number(kw.conversions || 0),
          tiklama: Number(kw.clicks || 0),
          gosterim: Number(kw.impressions || 0),
        };
      });
      const intentCols = [
        { label: 'Keyword', key: 'keyword' },
        { label: 'Intent', key: 'intent' },
        { label: 'Kampanya', key: 'kampanya' },
        { label: 'Harcama', key: 'harcama' },
        { label: 'Ciro', key: 'ciro' },
        { label: 'ROAS', key: 'roas' },
        { label: 'Dönüşüm', key: 'donusum' },
        { label: 'Tıklama', key: 'tiklama' },
        { label: 'Gösterim', key: 'gosterim' },
      ];
      csvAttachments.push({
        filename: `keyword-intent-kirilimi_${dateSuffix}.csv`,
        content: generateCsvContent(intentRows, intentCols, activeDateLabel),
      });
    }

    // Meta by Objective (funnel) CSV
    if (metaByObjective && metaByObjective.length > 0) {
      const moRows = metaByObjective.map((b) => ({
        funnel: b.label,
        harcama: Number(b.spend || 0).toFixed(2),
        ciro: Number(b.revenue || 0).toFixed(2),
        roas: b.spend > 0 ? (b.revenue / b.spend).toFixed(2) : '0',
        siparis: Number(b.orders || 0),
        tiklama: Number(b.clicks || 0),
        cr_yuzde: b.clicks > 0 ? ((b.orders / b.clicks) * 100).toFixed(2) : '0',
      }));
      const moCols = [
        { label: 'Funnel Adımı', key: 'funnel' },
        { label: 'Harcama', key: 'harcama' },
        { label: 'Ciro', key: 'ciro' },
        { label: 'ROAS', key: 'roas' },
        { label: 'Sipariş', key: 'siparis' },
        { label: 'Tıklama', key: 'tiklama' },
        { label: 'CR (%)', key: 'cr_yuzde' },
      ];
      csvAttachments.push({
        filename: `meta-funnel-kirilimi_${dateSuffix}.csv`,
        content: generateCsvContent(moRows, moCols, activeDateLabel),
      });
    }

    // Campaign × Item × Revenue (LIVE GA4) CSV — paid only
    if (campaignItemRows && campaignItemRows.length > 0) {
      const ciRows = [...campaignItemRows]
        .sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0))
        .map((r) => ({
          source: r.source || '',
          medium: r.medium || '',
          kampanya: r.campaignName || '',
          urun: r.itemName || '',
          ciro: Number(r.revenue || 0).toFixed(2),
          adet: Number(r.units || 0),
        }));
      const ciCols = [
        { label: 'Source', key: 'source' },
        { label: 'Medium', key: 'medium' },
        { label: 'Kampanya', key: 'kampanya' },
        { label: 'Ürün', key: 'urun' },
        { label: 'Ciro', key: 'ciro' },
        { label: 'Adet', key: 'adet' },
      ];
      csvAttachments.push({
        filename: `kampanya-urun-ciro_${dateSuffix}.csv`,
        content: generateCsvContent(ciRows, ciCols, activeDateLabel),
      });

      // Also push into email sections — top 20 campaigns aggregated
      const grouped = ciRows.reduce((acc, r) => {
        const key = `${r.source}/${r.medium} · ${r.kampanya}`;
        if (!acc[key]) acc[key] = { key, revenue: 0, units: 0, itemCount: 0 };
        acc[key].revenue += Number(r.ciro || 0);
        acc[key].units += Number(r.adet || 0);
        acc[key].itemCount += 1;
        return acc;
      }, {});
      const topCampaigns = Object.values(grouped)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 20);
      if (topCampaigns.length > 0) {
        sections.push({
          type: 'table',
          title: 'Kampanya × Ürün × Ciro (Canlı GA4 · Paid Only)',
          subtitle: `${ciRows.length} satır · Tam liste ekteki CSV'de`,
          columns: ['Platform · Kampanya', 'Ciro', 'Adet', 'Ürün Çeşidi'],
          rows: topCampaigns.map((c) => [c.key, fmt(c.revenue), fmt(c.units, 'number'), String(c.itemCount)]),
        });
      }
    }

    // Device Analysis CSV + email section
    if (deviceRows && deviceRows.length > 0) {
      const dvRows = deviceRows.map((d) => ({
        cihaz: d.device,
        oturum: Number(d.sessions || 0),
        kullanici: Number(d.users || 0),
        siparis: Number(d.transactions || 0),
        ciro: Number(d.revenue || 0).toFixed(2),
        cr_yuzde: Number(d.cr || 0).toFixed(2),
        aov: Number(d.aov || 0).toFixed(2),
      }));
      const dvCols = [
        { label: 'Cihaz', key: 'cihaz' },
        { label: 'Oturum', key: 'oturum' },
        { label: 'Kullanıcı', key: 'kullanici' },
        { label: 'Sipariş', key: 'siparis' },
        { label: 'Ciro', key: 'ciro' },
        { label: 'CR (%)', key: 'cr_yuzde' },
        { label: 'AOV', key: 'aov' },
      ];
      csvAttachments.push({
        filename: `cihaz-analizi_${dateSuffix}.csv`,
        content: generateCsvContent(dvRows, dvCols, activeDateLabel),
      });
      sections.push({
        type: 'table',
        title: 'Cihaz Analizi (Canlı GA4)',
        columns: ['Cihaz', 'Oturum', 'Sipariş', 'Ciro', 'CR (%)', 'AOV'],
        rows: deviceRows.map((d) => [d.device, fmt(d.sessions, 'number'), fmt(d.transactions, 'number'), fmt(d.revenue), `%${Number(d.cr || 0).toFixed(2)}`, fmt(d.aov)]),
      });
    }

    // New vs Returning CSV + email section
    if (newVsReturning && Array.isArray(newVsReturning.data) && newVsReturning.data.length > 0) {
      const newRow = newVsReturning.data.find((d) => String(d.userType || '').toLowerCase().includes('new')) || {};
      const retRow = newVsReturning.data.find((d) => String(d.userType || '').toLowerCase().includes('return')) || {};
      const newRev = Number(newRow.totalRevenue || 0);
      const retRev = Number(retRow.totalRevenue || 0);
      const newUsers = Number(newRow.totalUsers || 0);
      const retUsers = Number(retRow.totalUsers || 0);
      const totalRev = newRev + retRev;
      const newShare = totalRev > 0 ? (newRev / totalRev) * 100 : 0;
      const retShare = totalRev > 0 ? (retRev / totalRev) * 100 : 0;

      const nvrRows = [
        { tip: 'Yeni Müşteri', ciro: newRev.toFixed(2), kullanici: newUsers, pay_yuzde: newShare.toFixed(2) },
        { tip: 'Dönen Müşteri', ciro: retRev.toFixed(2), kullanici: retUsers, pay_yuzde: retShare.toFixed(2) },
        { tip: 'TOPLAM', ciro: totalRev.toFixed(2), kullanici: newUsers + retUsers, pay_yuzde: '100.00' },
      ];
      const nvrCols = [
        { label: 'Tip', key: 'tip' },
        { label: 'Ciro', key: 'ciro' },
        { label: 'Kullanıcı', key: 'kullanici' },
        { label: 'Pay (%)', key: 'pay_yuzde' },
      ];
      csvAttachments.push({
        filename: `yeni-vs-donen-musteri_${dateSuffix}.csv`,
        content: generateCsvContent(nvrRows, nvrCols, activeDateLabel),
      });
      sections.push({
        type: 'table',
        title: 'Yeni vs Dönen Müşteri Cirosu (Canlı GA4)',
        columns: ['Tip', 'Ciro', 'Kullanıcı', 'Pay (%)'],
        rows: [
          ['Yeni Müşteri', fmt(newRev), fmt(newUsers, 'number'), `%${newShare.toFixed(1)}`],
          ['Dönen Müşteri', fmt(retRev), fmt(retUsers, 'number'), `%${retShare.toFixed(1)}`],
        ],
      });
    }

    // ── CSV-only mode: bundle each CSV as its own .xlsx inside one ZIP ──
    if (mode === 'csv') {
      try {
        if (csvAttachments.length === 0) {
          setEmailResult({ success: false, message: 'Seçili dönem için indirilecek veri yok.' });
        } else {
          const zip = new JSZip();
          csvAttachments.forEach((att) => {
            // Convert each CSV string into a single-sheet .xlsx so files open
            // cleanly in Excel without import dialogs.
            const tempWb = XLSX.read(att.content, { type: 'string' });
            const xlsxBuf = XLSX.write(tempWb, { bookType: 'xlsx', type: 'array' });
            const xlsxName = att.filename.replace(/\.csv$/i, '.xlsx');
            zip.file(xlsxName, xlsxBuf);
          });
          const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
          const url = URL.createObjectURL(zipBlob);
          const a = document.createElement('a');
          a.href = url;
          const safeBrand = (brandName || 'rapor').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'rapor';
          a.download = `${safeBrand}_haftalik-rapor_${dateSuffix}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setEmailResult({
            success: true,
            message: `ZIP indirildi · ${csvAttachments.length} Excel dosyası (${activeDateLabel}).`,
          });
        }
      } finally {
        setEmailSending(false);
      }
      return;
    }

    try {
      const resp = await fetch(`${API_BASE}/api/google-ads/send-weekly-report-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          to: mode === 'email' ? emailRecipient.trim() : undefined,
          subject: `${brandName || 'Glowytics'} — ${periodLabelTr} Rapor (${activeDateLabel})`,
          sections,
          dateLabel: activeDateLabel,
          brandName: brandName || 'Glowytics',
          brandId,
          csvAttachments,
          exportHtml: mode === 'export',
        }),
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || 'İşlem başarısız');

      if (mode === 'export') {
        // Trigger HTML download
        const safeDate = (activeDateLabel || 'rapor').replace(/[^a-zA-Z0-9]/g, '-');
        const safeBrand = (brandName || 'glowytics').toLowerCase().replace(/[^a-z0-9]/g, '-');
        const filename = `weekly-report_${safeBrand}_${safeDate}.html`;
        const blob = new Blob([data.html || ''], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        // Also download CSV attachments individually so the user gets the raw data
        (csvAttachments || []).forEach((csv) => {
          const csvBlob = new Blob(['\uFEFF' + csv.content], { type: 'text/csv;charset=utf-8' });
          const csvUrl = URL.createObjectURL(csvBlob);
          const ca = document.createElement('a');
          ca.href = csvUrl;
          ca.download = csv.filename;
          document.body.appendChild(ca);
          ca.click();
          document.body.removeChild(ca);
          URL.revokeObjectURL(csvUrl);
        });
        setEmailResult({ success: true, message: `HTML raporu ve ${csvAttachments.length} CSV indirildi.` });
      } else {
        const allOk = data.results?.every((r) => r.success);
        setEmailResult({ success: allOk, message: allOk ? `Rapor başarıyla gönderildi: ${emailRecipient}` : `Kısmen gönderildi. Detay: ${JSON.stringify(data.results)}` });
      }
    } catch (err) {
      setEmailResult({ success: false, message: `Hata: ${err.message}` });
    } finally {
      setEmailSending(false);
    }
  }, [emailSending, emailRecipient, brandId, brandName, activeDateLabel, activeStartDate, activeEndDate, compareDateLabel, periodLabelTr,
    ga4Summary, compareGa4Summary, googleSummary, compareGoogleSummary, metaSummary, compareMetaSummary,
    combinedCampaigns, keywordsGrouped, brandVsGenericKeywords,
    creatives, creativeDetailsMap,
    itemsByProductLine, itemSummary, campaignColumns, keywordColumns, productLineColumns, topItemColumns,
    showCommentary, googleCampaignsBySegment, zeroSalesKeywords, keywordsByIntent, metaByObjective, budgetAllocation,
    aiSummary, bvgAnalysis, plAnalysis,
    generateBvgAnalysis, generatePlAnalysis]);

  const dateRangeOptions = [
    { value: 'last_week', label: weekRanges.last_week.label, group: 'Haftalık' },
    { value: 'this_week', label: weekRanges.this_week.label, group: 'Haftalık' },
    { value: 'two_weeks_ago', label: weekRanges.two_weeks_ago.label, group: 'Haftalık' },
    { value: 'last_month', label: monthRanges.last_month.label, group: 'Aylık' },
    { value: 'this_month', label: monthRanges.this_month.label, group: 'Aylık' },
    { value: 'two_months_ago', label: monthRanges.two_months_ago.label, group: 'Aylık' },
    { value: 'last30', label: 'Son 30 Gün', group: 'Diğer' },
    { value: 'custom', label: 'Özel Tarih Aralığı', group: 'Diğer' },
  ];

  // ── GlowyAI: "Ne yaptık – Ne oldu – Ne yapıyoruz" özeti ───���──────────────
  // Returns the produced summary text (so the email path can use it directly
  // without waiting for state updates).
  const generateAiSummary = useCallback(async () => {
    setAiSummaryLoading(true);
    setAiSummary('');

    const fmtN = (v) => (Number(v) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });

    // Top 3 worst & best performing campaigns (spend > 100) for action recommendations
    const significantCampaigns = combinedCampaigns.filter((c) => c.spend > 100);
    const top3Worst = [...significantCampaigns].sort((a, b) => a.roas - b.roas).slice(0, 3);
    const top3Best = [...significantCampaigns].sort((a, b) => b.roas - a.roas).slice(0, 3);
    const budgetMixLine = budgetAllocation
      ? `Mevcut bütçe dağılımı (toplam ₺${fmtN(budgetAllocation.total)}):
- Brand+PMAX: %${fmtN(budgetAllocation.actual.brandPmax)} (hedef %${budgetAllocation.target.brandPmax})
- Generic Search: %${fmtN(budgetAllocation.actual.generic)} (hedef %${budgetAllocation.target.generic})
- Meta Ads: %${fmtN(budgetAllocation.actual.meta)} (hedef %${budgetAllocation.target.meta})`
      : 'Bütçe dağılımı verisi yok';

    const dataContext = `
Dönem: ${activeDateLabel}
Karşılaştırma dönemi: ${compareDateLabel || 'Yok'}

GA4 Site Geneli:
- Ciro: ₺${fmtN(ga4Summary.revenue)} (önceki: ₺${fmtN(compareGa4Summary.revenue)})
- Sipariş: ${fmtN(ga4Summary.transactions)} (önceki: ${fmtN(compareGa4Summary.transactions)})
- Oturum: ${fmtN(ga4Summary.sessions)} (önceki: ${fmtN(compareGa4Summary.sessions)})
- Kullanıcı: ${fmtN(ga4Summary.users)} (önceki: ${fmtN(compareGa4Summary.users)})
- CR: %${fmtN(ga4Summary.cr)} (önceki: %${fmtN(compareGa4Summary.cr)})
- AOV: ₺${fmtN(ga4Summary.aov)} (önceki: ₺${fmtN(compareGa4Summary.aov)})

Google Ads (GA4 attribution — unified view):
- Harcama: ₺${fmtN(googleSummary.spend)} (önceki: ₺${fmtN(compareGoogleSummary.spend)})
- Ciro: ₺${fmtN(googleSummary.revenue)} (önceki: ₺${fmtN(compareGoogleSummary.revenue)})
- ROAS: ${fmtN(googleSummary.roas)}x (önceki: ${fmtN(compareGoogleSummary.roas)}x)
- Sipariş: ${fmtN(googleSummary.orders)} (önceki: ${fmtN(compareGoogleSummary.orders)})
- CR: %${fmtN(googleSummary.cr)} (önceki: %${fmtN(compareGoogleSummary.cr)})
- AOV: ₺${fmtN(googleSummary.aov)} (önceki: ₺${fmtN(compareGoogleSummary.aov)})

Meta Ads (GA4 attribution — unified view):
- Harcama: ₺${fmtN(metaSummary.spend)} (önceki: ₺${fmtN(compareMetaSummary.spend)})
- Ciro: ₺${fmtN(metaSummary.revenue)} (önceki: ₺${fmtN(compareMetaSummary.revenue)})
- ROAS: ${fmtN(metaSummary.roas)}x (önceki: ${fmtN(compareMetaSummary.roas)}x)
- Sipariş: ${fmtN(metaSummary.orders)} (önceki: ${fmtN(compareMetaSummary.orders)})
- CR: %${fmtN(metaSummary.cr)} (önceki: %${fmtN(compareMetaSummary.cr)})
- AOV: ₺${fmtN(metaSummary.aov)} (önceki: ₺${fmtN(compareMetaSummary.aov)})

${budgetMixLine}

En kötü performans (ROAS düşük, harcama > ₺100):
${top3Worst.map((c) => `- ${c.platform} | ${c.name} | Harcama: ₺${fmtN(c.spend)} | Ciro: ₺${fmtN(c.revenue)} | ROAS: ${fmtN(c.roas)}x | Sipariş: ${fmtN(c.orders)}`).join('\n') || '- Yok'}

En iyi performans (ROAS yüksek, harcama > ₺100):
${top3Best.map((c) => `- ${c.platform} | ${c.name} | Harcama: ₺${fmtN(c.spend)} | Ciro: ₺${fmtN(c.revenue)} | ROAS: ${fmtN(c.roas)}x | Sipariş: ${fmtN(c.orders)}`).join('\n') || '- Yok'}
`.trim();

    try {
      const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          model: 'gemini-2.5-flash',
          temperature: 0.4,
          maxOutputTokens: 8192,
          messages: [
            {
              role: 'user',
              content: `Sen bir dijital pazarlama analistisin. Telefon ve elektronik sektöründe çalışan bir e-ticaret markasının ${periodLabelTr.toLowerCase()} performans verilerini yorumluyorsun.

Sektör bilgisi (yorumlarında bunu göz önünde bulundur):
- Bu marka telefon/elektronik sektöründe faaliyet gösteriyor. Satın alma süreci uzundur (4-5 güne yayılır).
- Müşteriler Meta reklamını görür, araştırır, sonra Google'da marka adını arayarak (brand search) siteye gelip satın alır.
- Bu nedenle Meta Ads, GA4 last-click attribution modelinde düşük değer alır. Meta'nın gerçek katkısı attribution modelinin gösterdiğinden daha yüksektir.
- Meta'yı değerlendirirken sadece last-click ROAS'a bakma, assisted conversion etkisini de göz önünde bulundur.
- Düşük Meta ROAS'ını hemen "kötü performans" olarak yorumlama — sektör dinamiğini açıkla.

Türkçe, madde madde, 2 başlık altında yaz:

Ne Oldu;
(GA4 site geneli, Google Ads ve Meta Ads performans sonuçları — önceki döneme göre artış/azalış yüzdeleriyle birlikte. Maks 4 madde.)

Öne Çıkanlar;
(dikkat çeken pozitif veya negatif trendler — ROAS değişimleri, AOV hareketleri, CR farkları, en iyi/en kötü kampanyalar. Maks 3 madde.)

Kurallar:
- Başlıkları kalın (bold/yıldız) yapma. Düz yazı olarak "Ne Oldu;" şeklinde yaz.
- Kısa ve net yaz, her madde 1-2 cümle.
- Rakamları ve yüzdeleri mutlaka kullan.
- Yorum yap ama spekülasyon yapma — sadece verideki gerçekleri yorumla.
- Meta performansını yorumlarken attribution model farkını ve sektörün uzun satın alma sürecini mutlaka belirt.
- Aksiyon/öneri yazma — sadece neler oldu ve ne öne çıktı. "Bütçeyi artır", "Negative ekle", "Kampanyayı durdur" gibi öneriler yasak.

VERİLER:
${dataContext}`
            }
          ]
        })
      });

      if (!resp.ok) throw new Error('AI yanıt veremedi');
      const data = await resp.json();
      const text = data?.message || data?.text || data?.answer || 'Yanıt alınamadı.';
      setAiSummary(text);
      return text;
    } catch {
      const text = 'AI özeti oluşturulamadı. Lütfen tekrar deneyin.';
      setAiSummary(text);
      return text;
    } finally {
      setAiSummaryLoading(false);
    }
  }, [activeDateLabel, compareDateLabel, periodLabelTr, ga4Summary, compareGa4Summary, googleSummary, compareGoogleSummary, metaSummary, compareMetaSummary, combinedCampaigns, budgetAllocation, brandId]);

  // Keep the ref pointing to the latest generator so sendReportEmail (declared
  // earlier in this component) can call it without taking it as a dependency.
  generateAiSummaryRef.current = generateAiSummary;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f6f8ff] print:bg-white">
      {/* Print styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:break-inside-avoid { break-inside: avoid; }
          body { font-size: 11px; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
        }
      `}</style>

      <div className="max-w-screen-2xl mx-auto px-4 py-6 print:px-0 print:py-2">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8 print:mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 print:text-xl">
              {periodLabelTr} Yönetici Raporu
            </h1>
            <p className="text-sm font-medium text-indigo-600 mt-1">
              {activeDateLabel}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Tüm platformlara ait performans özeti
              {compareDateLabel && (
                <span className="ml-1 text-gray-400">
                  · Karşılaştırma: {compareDateLabel}
                </span>
              )}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 print:hidden">
            {/* Date range selector */}
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
              <Calendar size={14} className="text-gray-400" />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="text-sm text-gray-700 bg-transparent outline-none cursor-pointer"
              >
                {['Haftalık', 'Aylık', 'Diğer'].map((groupName) => {
                  const opts = dateRangeOptions.filter((o) => o.group === groupName);
                  if (!opts.length) return null;
                  return (
                    <optgroup key={groupName} label={groupName}>
                      {opts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            </div>

            {dateRange === 'custom' && (
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="text-sm text-gray-700 bg-transparent outline-none"
                />
                <span className="text-gray-400">–</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="text-sm text-gray-700 bg-transparent outline-none"
                />
              </div>
            )}

            {/* Print button */}
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded-xl shadow-sm transition-colors font-medium"
            >
              <Printer size={14} />
              Yazdır
            </button>

            {/* Export HTML button — same content as email, downloadable */}
            <button
              onClick={() => sendReportEmail('export')}
              disabled={emailSending}
              className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-xl shadow-sm transition-colors font-medium"
              title="Mail ile gönderilecek aynı içeriği HTML + CSV olarak indir"
            >
              <Printer size={14} />
              {emailSending ? 'Hazırlanıyor…' : 'HTML İndir'}
            </button>

            {/* Excel (CSV) download button — selected week only, no email/HTML */}
            <button
              onClick={() => sendReportEmail('csv')}
              disabled={emailSending}
              className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-xl shadow-sm transition-colors font-medium"
              title={`Seçili dönem (${activeDateLabel}) için tüm Excel dosyalarını ZIP olarak indir`}
            >
              <Download size={14} />
              {emailSending ? 'Hazırlanıyor…' : 'Excel İndir'}
            </button>

            {/* Email button */}
            <button
              onClick={() => { setEmailResult(null); setShowEmailModal(true); }}
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 rounded-xl shadow-sm transition-colors font-medium"
            >
              <Mail size={14} />
              Mail Gönder
            </button>
          </div>
        </div>

        {/* ── Attribution Disclosure Banner ─────────────────────────────── */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg print:break-inside-avoid">
          <h3 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
            <BarChart3 size={14} />
            Veri Kaynakları &amp; Attribution
          </h3>
          <ul className="text-xs text-blue-900 space-y-1 leading-relaxed">
            <li><b>GA4 — Site Geneli:</b> ga_traffic_source_raw — site geneli organik+ücretli tüm trafik (Last-click attribution).</li>
            <li><b>Google Ads &amp; Meta Ads (Üst Özet ve Kampanya Tablosu):</b> unified_vw_campaign_daily_ga_with_costs_include_meta — GA4 last-click attribution. Harcama Google/Meta panelinden, satış GA4'ten.</li>
            <li><b>Meta Kreatif Performansı:</b> meta_ad_performance_with_creative_vw — Meta native data-driven attribution.</li>
            <li><b>Keyword Performansı:</b> google_ads_keyword_full_performance — Google Ads native data-driven attribution.</li>
            <li className="font-semibold text-amber-700 pt-1">⚠️ GA4 ve Meta native ROAS rakamları farklılık gösterebilir — Meta'nın gerçek katkısı GA4 last-click'in gösterdiğinden yüksektir (assisted conversion).</li>
          </ul>
        </div>

        {/* ── Section 1: Executive Summary Cards ─────────────────────────── */}
        <Section title="Yönetici Özeti" dateLabel={activeDateLabel}>
          {/* GA4 Site Geneli */}
          {ga4Loading ? (
            <LoadingSpinner />
          ) : ga4Error ? (
            <ErrorFallback message={ga4Error} />
          ) : (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                GA4 — Site Geneli
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <SummaryCard
                  label="Toplam Ciro"
                  value={fmt(ga4Summary.revenue)}
                  delta={pct(ga4Summary.revenue, compareGa4Summary.revenue)}
                  icon={DollarSign}
                  color="emerald"
                />
                <SummaryCard
                  label="Sipariş"
                  value={fmt(ga4Summary.transactions, 'number')}
                  delta={pct(ga4Summary.transactions, compareGa4Summary.transactions)}
                  icon={ShoppingCart}
                  color="amber"
                />
                <SummaryCard
                  label="Oturum"
                  value={fmt(ga4Summary.sessions, 'number')}
                  delta={pct(ga4Summary.sessions, compareGa4Summary.sessions)}
                  icon={Globe}
                  color="blue"
                />
                <SummaryCard
                  label="Kullanıcı"
                  value={fmt(ga4Summary.users, 'number')}
                  delta={pct(ga4Summary.users, compareGa4Summary.users)}
                  icon={Users}
                  color="indigo"
                />
                <SummaryCard
                  label="CR (%)"
                  value={fmt(ga4Summary.cr, 'percent')}
                  delta={pct(ga4Summary.cr, compareGa4Summary.cr)}
                  icon={Target}
                  color="violet"
                />
                <SummaryCard
                  label="AOV"
                  value={fmt(ga4Summary.aov)}
                  delta={pct(ga4Summary.aov, compareGa4Summary.aov)}
                  icon={DollarSign}
                  color="rose"
                />
              </div>
            </div>
          )}

          {unifiedLoading ? (
            <LoadingSpinner />
          ) : unifiedError ? (
            <ErrorFallback message={unifiedError} />
          ) : (
            <div className="space-y-4">
              {/* Google Ads */}
              <div>
                <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                  Google Ads <span className="text-[10px] text-gray-400 font-normal">(GA4 attribution)</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <SummaryCard
                    label="Harcama"
                    value={fmt(googleSummary.spend)}
                    delta={pct(googleSummary.spend, compareGoogleSummary.spend)}
                    icon={DollarSign}
                    color="blue"
                  />
                  <SummaryCard
                    label="Ciro (GA4)"
                    value={fmt(googleSummary.revenue)}
                    delta={pct(googleSummary.revenue, compareGoogleSummary.revenue)}
                    icon={TrendingUp}
                    color="emerald"
                  />
                  <SummaryCard
                    label="ROAS (GA4)"
                    value={fmt(googleSummary.roas, 'roas')}
                    delta={pct(googleSummary.roas, compareGoogleSummary.roas)}
                    icon={BarChart3}
                    color="indigo"
                  />
                  <SummaryCard
                    label="Sipariş (GA4)"
                    value={fmt(googleSummary.orders, 'number')}
                    delta={pct(googleSummary.orders, compareGoogleSummary.orders)}
                    icon={ShoppingCart}
                    color="amber"
                  />
                  <SummaryCard
                    label="CR (%)"
                    value={fmt(googleSummary.cr, 'percent')}
                    delta={pct(googleSummary.cr, compareGoogleSummary.cr)}
                    icon={Target}
                    color="violet"
                  />
                  <SummaryCard
                    label="AOV"
                    value={fmt(googleSummary.aov)}
                    delta={pct(googleSummary.aov, compareGoogleSummary.aov)}
                    icon={DollarSign}
                    color="rose"
                  />
                </div>
              </div>

              {/* Brand Health Card (M9) */}
              {(() => {
                const brandSeg = googleCampaignsBySegment.find((s) => s.key === 'brand');
                if (!brandSeg) return null;
                const brandCpc = brandSeg.clicks > 0 ? brandSeg.spend / brandSeg.clicks : 0;
                return (
                  <div>
                    <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                      Brand Sağlık Kartı <span className="text-[10px] text-gray-400 font-normal">(Brand Search segmenti)</span>
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      <SummaryCard label="Brand Harcama" value={fmt(brandSeg.spend)} icon={DollarSign} color="blue" />
                      <SummaryCard label="Brand CPC" value={fmt(brandCpc)} icon={Target} color="amber" />
                      <SummaryCard label="Brand CR (%)" value={fmt(brandSeg.cr, 'percent')} icon={Target} color="violet" />
                      <SummaryCard label="Brand AOV" value={fmt(brandSeg.aov)} icon={DollarSign} color="rose" />
                      <SummaryCard label="Brand ROAS" value={fmt(brandSeg.roas, 'roas')} icon={BarChart3} color="emerald" />
                    </div>
                  </div>
                );
              })()}

              {/* Meta Ads */}
              <div>
                <h3 className="text-sm font-semibold text-indigo-700 mb-2 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-indigo-500 inline-block" />
                  Meta Ads <span className="text-[10px] text-gray-400 font-normal">(GA4 attribution)</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <SummaryCard
                    label="Harcama"
                    value={fmt(metaSummary.spend)}
                    delta={pct(metaSummary.spend, compareMetaSummary.spend)}
                    icon={DollarSign}
                    color="blue"
                  />
                  <SummaryCard
                    label="Ciro (GA4)"
                    value={fmt(metaSummary.revenue)}
                    delta={pct(metaSummary.revenue, compareMetaSummary.revenue)}
                    icon={TrendingUp}
                    color="emerald"
                  />
                  <SummaryCard
                    label="ROAS (GA4)"
                    value={fmt(metaSummary.roas, 'roas')}
                    delta={pct(metaSummary.roas, compareMetaSummary.roas)}
                    icon={BarChart3}
                    color="indigo"
                  />
                  <SummaryCard
                    label="Sipariş (GA4)"
                    value={fmt(metaSummary.orders, 'number')}
                    delta={pct(metaSummary.orders, compareMetaSummary.orders)}
                    icon={ShoppingCart}
                    color="amber"
                  />
                  <SummaryCard
                    label="CR (%)"
                    value={fmt(metaSummary.cr, 'percent')}
                    delta={pct(metaSummary.cr, compareMetaSummary.cr)}
                    icon={Target}
                    color="violet"
                  />
                  <SummaryCard
                    label="AOV"
                    value={fmt(metaSummary.aov)}
                    delta={pct(metaSummary.aov, compareMetaSummary.aov)}
                    icon={DollarSign}
                    color="rose"
                  />
                </div>
              </div>
            </div>
          )}
        </Section>


        {/* ── Section 2: Campaign Performance — Google segmented + Meta ─── */}
        <Section title="Kampanya Bazlı Performans (Google + Meta)" dateLabel={activeDateLabel}>
          {(googleCampaignsLoading || metaCampaignsLoading || unifiedLoading) ? (
            <LoadingSpinner />
          ) : (googleCampaignsError || metaCampaignsError) ? (
            <ErrorFallback message={googleCampaignsError || metaCampaignsError} />
          ) : (
            <>
              {/* Google segmentation sub-tables */}
              {googleCampaignsBySegment.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-blue-700 mb-3 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                    Google Ads — Segment Bazında (GA4 Attribution)
                  </h3>
                  <div className="space-y-4">
                    {googleCampaignsBySegment.map((seg) => (
                      <div key={seg.key} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print:break-inside-avoid">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <h4 className="text-sm font-bold text-gray-800">{seg.label}</h4>
                            <span className="text-[10px] text-gray-500 uppercase">{seg.rows.length} kampanya · %{seg.spendShare.toFixed(1)} pay</span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 text-xs">
                            <div><span className="text-gray-500">Harcama:</span> <span className="font-semibold">{fmt(seg.spend)}</span></div>
                            <div><span className="text-gray-500">Ciro:</span> <span className="font-semibold text-emerald-700">{fmt(seg.revenue)}</span></div>
                            <div><span className="text-gray-500">ROAS:</span> <span className={`font-semibold ${seg.roas >= 3 ? 'text-emerald-700' : seg.roas >= 1 ? 'text-amber-700' : 'text-red-700'}`}>{fmt(seg.roas, 'roas')}</span></div>
                            <div><span className="text-gray-500">Sipariş:</span> <span className="font-semibold">{fmt(seg.orders, 'number')}</span></div>
                            <div><span className="text-gray-500">CR:</span> <span className="font-semibold">{fmt(seg.cr, 'percent')}</span></div>
                          </div>
                        </div>
                        <div className="p-3">
                          <SortableTable
                            rows={seg.rows}
                            columns={campaignColumns}
                            filename={`google-${seg.key}-kampanya.csv`}
                            dateLabel={activeDateLabel}
                            initialSort={{ key: 'spend', dir: 'desc' }}
                            pageSize={10}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Combined table (all platforms) — useful for full export */}
              <h3 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" />
                Tüm Kampanyalar (Google + Meta) — Detaylı Tablo
              </h3>
              <SortableTable
                rows={combinedCampaigns}
                columns={campaignColumns}
                filename="kampanya-performansi.csv"
                dateLabel={activeDateLabel}
                initialSort={{ key: 'spend', dir: 'desc' }}
              />
            </>
          )}

        </Section>

        {/* ── Section 2b: Sıfır Ciro Keyword Temizliği ─────────────────── */}
        {zeroSalesKeywords.length > 0 && (
          <Section title="🧹 Sıfır Ciro Keyword Temizliği" dateLabel={activeDateLabel}>
            <p className="text-sm text-gray-600 mb-3">
              Bu dönemde harcama yapan ama 0 dönüşüm getiren <b>{zeroSalesKeywords.length}</b> keyword.
              Toplam boşa giden harcama: <b>{fmt(zeroSalesKeywords.reduce((s, k) => s + Number(k.cost), 0))}</b>.
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600">Keyword</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600">Kampanya</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">Harcama</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">Tıklama</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">Gösterim</th>
                  </tr>
                </thead>
                <tbody>
                  {zeroSalesKeywords.map((kw, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-700">{kw.keyword_text}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{kw.campaign_name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(kw.cost)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(kw.clicks, 'number')}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(kw.impressions, 'number')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </Section>
        )}

        {/* ── Section 3: Keyword Table (grouped) ─────────────────────────── */}
        <Section title="Anahtar Kelime Performansı (Google Ads native)" dateLabel={activeDateLabel}>
          <p className="text-xs text-gray-400 mb-3">{keywords.length} satır → {keywordsGrouped.length} keyword olarak gruplandı</p>

          {/* Keyword Intent breakdown cards */}
          {keywordsByIntent && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {[
                { key: 'high', data: keywordsByIntent.high, color: 'emerald', headerCls: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
                { key: 'medium', data: keywordsByIntent.medium, color: 'amber', headerCls: 'bg-amber-50 border-amber-200 text-amber-800' },
                { key: 'low', data: keywordsByIntent.low, color: 'rose', headerCls: 'bg-rose-50 border-rose-200 text-rose-800' },
              ].map(({ key, data, headerCls }) => (
                <div key={key} className={`rounded-xl border ${headerCls} p-4 shadow-sm print:shadow-none print:break-inside-avoid`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold">{data.label}</h4>
                    <span className="text-[10px] uppercase font-semibold">{data.count} keyword</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="opacity-60">Harcama:</span> <span className="font-semibold">{fmt(data.spend)}</span></div>
                    <div><span className="opacity-60">Ciro:</span> <span className="font-semibold">{fmt(data.revenue)}</span></div>
                    <div><span className="opacity-60">ROAS:</span> <span className="font-semibold">{fmt(data.roas, 'roas')}</span></div>
                    <div><span className="opacity-60">Sipariş:</span> <span className="font-semibold">{fmt(data.orders, 'number')}</span></div>
                    <div><span className="opacity-60">CR:</span> <span className="font-semibold">{fmt(data.cr, 'percent')}</span></div>
                    <div><span className="opacity-60">AOV:</span> <span className="font-semibold">{fmt(data.aov)}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <SortableTable
            rows={keywordsGrouped}
            columns={keywordColumns}
            filename="anahtar-kelime-performansi.csv"
            dateLabel={activeDateLabel}
            initialSort={{ key: 'cost', dir: 'desc' }}
            isLoading={keywordsLoading}
            error={keywordsError}
          />

        </Section>

        {/* ── Section 4: Meta Creative Performance — inline previews ────── */}
        <Section title="Kreatif Performansı (Meta)" dateLabel={activeDateLabel}>
          {creativesLoading ? (
            <LoadingSpinner />
          ) : creativesError ? (
            <ErrorFallback message={creativesError} />
          ) : creatives.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">Kreatif verisi bulunamadı.</p>
          ) : (
            <>
              {/* Meta funnel split (by campaign objective) */}
              {metaByObjective && metaByObjective.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Meta Funnel Kırılımı (Kampanya Hedefine Göre)
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {metaByObjective.map((b) => (
                      <div key={b.key} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                        <p className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide mb-1">{b.label}</p>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
                          <span className="text-gray-400">Harcama</span>
                          <span className="text-right font-semibold text-gray-800">{fmt(b.spend)}</span>
                          <span className="text-gray-400">Ciro</span>
                          <span className="text-right font-semibold text-emerald-600">{fmt(b.revenue)}</span>
                          <span className="text-gray-400">Sipariş</span>
                          <span className="text-right font-semibold text-gray-800">{fmt(b.orders, 'number')}</span>
                          <span className="text-gray-400">ROAS</span>
                          <span className="text-right font-semibold text-indigo-600">{b.spend > 0 ? fmt(b.roas, 'roas') : '-'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Kaynak: meta_campaign_performance · Meta native attribution
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-400 mb-3">
                {creatives.length} kreatif · Satışa göre sıralı (top 20)
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {creatives
                  .sort((a, b) => b.purchases - a.purchases)
                  .slice(0, 20)
                  .map((c) => {
                    const det = creativeDetailsMap[c.creative_id];
                    const title = det?.title || det?.name || c.ad_name || c.creative_id;
                    const body = det?.body || '';
                    const isVideo = det?.is_video;
                    const mediaUrl =
                      det?.best_media_url ||
                      det?.enhanced_image_url ||
                      det?.image_url ||
                      det?.video_thumbnail_url ||
                      det?.thumbnail_url ||
                      '';
                    let linkDomain = '';
                    try {
                      if (det?.link_url) linkDomain = new URL(det.link_url).hostname.replace(/^www\./, '');
                    } catch { /* ignore */ }
                    return (
                      <div key={c.creative_id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print:break-inside-avoid">
                        {/* Preview area */}
                        <div className="w-full bg-gray-100 relative" style={{ paddingBottom: '100%', overflow: 'hidden' }}>
                          {mediaUrl ? (
                            <>
                              <img
                                src={mediaUrl}
                                alt={title}
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                              {isVideo && (
                                <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                                  VIDEO
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-300">
                              <Eye size={28} />
                              <span className="text-xs mt-1">Önizleme yok</span>
                            </div>
                          )}
                        </div>
                        {/* Ad copy */}
                        {(body || linkDomain) && (
                          <div className="px-4 pt-3 pb-2 border-t border-gray-100">
                            {body && (
                              <p className="text-xs text-gray-600 line-clamp-3" title={body} style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>{body}</p>
                            )}
                            {linkDomain && (
                              <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-1">{linkDomain}</p>
                            )}
                          </div>
                        )}
                        {/* Metrics */}
                        <div className="px-4 py-3 border-t border-gray-100">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-800 truncate" title={title}>{title}</p>
                              {c.campaign_name && campaignObjectiveMap[c.campaign_name] && (
                                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
                                  campaignObjectiveMap[c.campaign_name] === 'Sales' ? 'bg-emerald-100 text-emerald-700' :
                                  campaignObjectiveMap[c.campaign_name] === 'Awareness' ? 'bg-violet-100 text-violet-700' :
                                  campaignObjectiveMap[c.campaign_name] === 'Engagement' ? 'bg-indigo-100 text-indigo-700' :
                                  campaignObjectiveMap[c.campaign_name] === 'Traffic' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>{campaignObjectiveMap[c.campaign_name]}</span>
                              )}
                            </div>
                            <ActionBadge action={getCreativeAction({ spend: c.spend, revenue: c.revenue, purchases: c.purchases })} />
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase">Satış</p>
                              <p className="text-sm font-bold text-gray-800">{fmt(c.purchases, 'number')}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase">Ciro</p>
                              <p className="text-sm font-bold text-emerald-600">{fmt(c.revenue)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase">ROAS</p>
                              <p className="text-sm font-bold text-indigo-600">{c.spend > 0 ? fmt(c.revenue / c.spend, 'roas') : '-'}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase">Harcama</p>
                              <p className="text-xs font-medium text-gray-600">{fmt(c.spend)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase">CTR</p>
                              <p className="text-xs font-medium text-gray-600">{c.impressions > 0 ? fmt((c.clicks / c.impressions) * 100, 'percent') : '-'}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase">CR</p>
                              <p className="text-xs font-medium text-gray-600">{c.clicks > 0 ? fmt((c.purchases / c.clicks) * 100, 'percent') : '-'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}

        </Section>

        {/* ── Section 5: Brand vs Generic — Keyword Bazlı Deep Analysis ── */}
        {brandVsGenericKeywords && (
          <Section title="Brand vs Generic — Keyword Bazlı Analiz" dateLabel={activeDateLabel}>
            {/* CSV export + info bar */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-500 font-medium">
                {brandVsGenericKeywords.totalKeywords} keyword analiz edildi · Marka: <span className="font-semibold text-gray-700">{brandName}</span>
              </p>
              <button
                onClick={() => {
                  const bn = brandName.toLowerCase().trim();
                  const variants = [bn, bn.replace(/\s+/g, ''), bn.replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ç/g, 'c').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ı/g, 'i')];
                  if (bn.length > 4) variants.push(bn.slice(0, 4));
                  const rows = keywordsGrouped.map((kw) => {
                    const kwText = (kw.keyword_text || '').toLowerCase();
                    const isBrand = variants.some((v) => kwText.includes(v));
                    const spend = Number(kw.cost || 0);
                    const revenue = Number(kw.conversion_value || 0);
                    const roas = spend > 0 ? (revenue / spend).toFixed(2) : '0';
                    const clicks = Number(kw.clicks || 0);
                    const impressions = Number(kw.impressions || 0);
                    const conversions = Number(kw.conversions || 0);
                    const cr = clicks > 0 ? ((conversions / clicks) * 100).toFixed(2) : '0';
                    const ctr = impressions > 0 ? ((clicks / impressions) * 100).toFixed(2) : '0';
                    return [
                      kw.keyword_text,
                      isBrand ? 'Brand' : 'Generic',
                      kw.campaign_name,
                      spend.toFixed(2),
                      revenue.toFixed(2),
                      roas,
                      conversions,
                      clicks,
                      impressions,
                      cr,
                      ctr,
                    ];
                  });
                  const header = ['Keyword', 'Tip', 'Kampanya', 'Harcama', 'Ciro', 'ROAS', 'Sipariş', 'Tıklama', 'Gösterim', 'CR (%)', 'CTR (%)'];
                  const csvLines = [
                    `"Rapor Dönemi: ${activeDateLabel}"`,
                    `"Marka: ${brandName}"`,
                    '',
                    header.join(','),
                    ...rows.map((r) => r.map((v) => {
                      const s = String(v);
                      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
                    }).join(','))
                  ];
                  const csv = csvLines.join('\n');
                  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `brand-vs-generic-keywords_${(activeStartDate && activeEndDate) ? `${activeStartDate}_${activeEndDate}` : (activeDateLabel || 'rapor').replace(/[^a-zA-Z0-9]/g, '-')}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="inline-flex items-center gap-1.5 text-xs bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 px-3 py-1.5 rounded-lg transition-colors print:hidden"
              >
                <Download size={12} />
                CSV İndir
              </button>
            </div>
            {/* Distribution bars */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mb-4">
              <div className="space-y-3">
                {/* Spend distribution */}
                <div>
                  <p className="text-[10px] text-gray-400 uppercase mb-1">Harcama Dağılımı</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-blue-600 font-semibold w-20">Brand %{brandVsGenericKeywords.brand.spendShare.toFixed(1)}</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden flex">
                      <div className="bg-blue-500 h-full transition-all duration-500" style={{ width: `${brandVsGenericKeywords.brand.spendShare}%` }} />
                      <div className="bg-violet-500 h-full transition-all duration-500" style={{ width: `${brandVsGenericKeywords.generic.spendShare}%` }} />
                    </div>
                    <span className="text-xs text-violet-600 font-semibold w-24 text-right">Generic %{brandVsGenericKeywords.generic.spendShare.toFixed(1)}</span>
                  </div>
                </div>
                {/* Revenue distribution */}
                <div>
                  <p className="text-[10px] text-gray-400 uppercase mb-1">Ciro Dağılımı</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-blue-600 font-semibold w-20">Brand %{brandVsGenericKeywords.brand.revenueShare.toFixed(1)}</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden flex">
                      <div className="bg-blue-400 h-full transition-all duration-500" style={{ width: `${brandVsGenericKeywords.brand.revenueShare}%` }} />
                      <div className="bg-violet-400 h-full transition-all duration-500" style={{ width: `${brandVsGenericKeywords.generic.revenueShare}%` }} />
                    </div>
                    <span className="text-xs text-violet-600 font-semibold w-24 text-right">Generic %{brandVsGenericKeywords.generic.revenueShare.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Two side-by-side metric cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {[
                { key: 'brand', data: brandVsGenericKeywords.brand, label: 'Brand Keywords', color: 'blue' },
                { key: 'generic', data: brandVsGenericKeywords.generic, label: 'Generic Keywords', color: 'violet' },
              ].map(({ key, data, label, color }) => (
                <div key={key} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm print:shadow-none">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`text-sm font-bold ${color === 'blue' ? 'text-blue-700' : 'text-violet-700'}`}>
                      {label}
                    </h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color === 'blue' ? 'bg-blue-50 text-blue-600' : 'bg-violet-50 text-violet-600'}`}>
                      {data.count} keyword
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs text-gray-500">Harcama</p>
                      <p className="font-semibold text-gray-800">{fmt(data.spend)}</p>
                      <p className="text-[10px] text-gray-400">%{data.spendShare.toFixed(1)} pay</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Ciro</p>
                      <p className="font-semibold text-gray-800">{fmt(data.revenue)}</p>
                      <p className="text-[10px] text-gray-400">%{data.revenueShare.toFixed(1)} pay</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">ROAS</p>
                      <p className={`font-semibold ${data.roas >= 3 ? 'text-emerald-600' : data.roas >= 1 ? 'text-amber-600' : 'text-red-600'}`}>
                        {fmt(data.roas, 'roas')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Sipariş</p>
                      <p className="font-semibold text-gray-800">{fmt(data.orders, 'number')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">CR (%)</p>
                      <p className="font-semibold text-gray-800">{fmt(data.cr, 'percent')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">CTR (%)</p>
                      <p className="font-semibold text-gray-800">{fmt(data.ctr, 'percent')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">AOV</p>
                      <p className="font-semibold text-gray-800">{fmt(data.aov)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Gösterim</p>
                      <p className="font-semibold text-gray-800">{fmt(data.impressions, 'number')}</p>
                    </div>
                  </div>
                  {/* Top keywords preview */}
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <p className="text-[10px] text-gray-400 uppercase mb-1">Örnek Keywordler</p>
                    <div className="flex flex-wrap gap-1">
                      {data.keywords.slice(0, 5).map((kw, i) => (
                        <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${color === 'blue' ? 'bg-blue-50 text-blue-600' : 'bg-violet-50 text-violet-600'}`}>
                          {kw.keyword_text}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* AI Deep Analysis button & output */}
            {!bvgAnalysis && !bvgAnalysisLoading && (
              <button
                onClick={generateBvgAnalysis}
                disabled={keywordsLoading}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 disabled:opacity-50 text-white text-sm px-5 py-2.5 rounded-xl shadow-sm transition-all font-medium"
              >
                <Sparkles size={16} />
                GlowyAI ile Brand vs Generic Analiz Oluştur
              </button>
            )}
            {bvgAnalysisLoading && (
              <div className="flex items-center gap-3 py-6">
                <Loader2 className="animate-spin text-violet-500" size={20} />
                <span className="text-sm text-gray-500">GlowyAI derinlemesine analiz yapıyor...</span>
              </div>
            )}
            {bvgAnalysis && !bvgAnalysisLoading && (
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={14} className="text-violet-500" />
                  <h4 className="text-sm font-bold text-gray-800">Brand vs Generic — Derinlemesine Analiz</h4>
                </div>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {bvgAnalysis}
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100 print:hidden">
                  <button
                    onClick={generateBvgAnalysis}
                    className="inline-flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-800 font-medium"
                  >
                    <Sparkles size={12} />
                    Yeniden Oluştur
                  </button>
                  <span className="text-xs text-gray-300">|</span>
                  <span className="text-xs text-gray-400">GlowyAI (Gemini 2.5 Flash) ile oluşturuldu</span>
                </div>
              </div>
            )}

          </Section>
        )}

        {/* ── Section 6: Product / Category Breakdown ────────────────────── */}
        <Section title="Ürün Kategori Kırılımı" dateLabel={activeDateLabel}>
          <p className="text-xs text-gray-400 mb-2">{itemSummary.length} ürün → {itemsByCategory.length} kategoriye gruplandı</p>
          <SortableTable
            rows={itemsByCategory}
            columns={itemColumns}
            filename="urun-kategori-kirilimi.csv"
            dateLabel={activeDateLabel}
            initialSort={{ key: 'revenue', dir: 'desc' }}
            isLoading={itemSummaryLoading}
            error={itemSummaryError}
          />
        </Section>

        {/* ── Section 7: Product Line Breakdown ────────────────────────── */}
        {itemsByProductLine.length > 0 && (
          <Section title="Ürün Ailesi Kırılımı (iPhone / Mac / iPad / Watch)" dateLabel={activeDateLabel}>
            <p className="text-xs text-gray-400 mb-2">Aksesuar ve küçük ürünler hariç · Sadece ana cihazlar</p>
            <SortableTable
              rows={itemsByProductLine}
              columns={productLineColumns}
              filename="urun-ailesi-kirilimi.csv"
              dateLabel={activeDateLabel}
              initialSort={{ key: 'revenue', dir: 'desc' }}
              isLoading={itemSummaryLoading}
              error={itemSummaryError}
            />

            {/* AI Product Line Growth Analysis */}
            <div className="mt-4">
              {!plAnalysis && !plAnalysisLoading && (
                <button
                  onClick={generatePlAnalysis}
                  disabled={itemSummaryLoading}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 text-white text-sm px-5 py-2.5 rounded-xl shadow-sm transition-all font-medium"
                >
                  <Sparkles size={16} />
                  GlowyAI ile Kârlı Büyüme Analizi Oluştur
                </button>
              )}
              {plAnalysisLoading && (
                <div className="flex items-center gap-3 py-6">
                  <Loader2 className="animate-spin text-emerald-500" size={20} />
                  <span className="text-sm text-gray-500">GlowyAI ürün ailesi analizi yapıyor...</span>
                </div>
              )}
              {plAnalysis && !plAnalysisLoading && (
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={14} className="text-emerald-500" />
                    <h4 className="text-sm font-bold text-gray-800">Ürün Ailesi — Kârlı Büyüme Stratejisi</h4>
                  </div>
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {plAnalysis}
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100 print:hidden">
                    <button
                      onClick={generatePlAnalysis}
                      className="inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                    >
                      <Sparkles size={12} />
                      Yeniden Oluştur
                    </button>
                    <span className="text-xs text-gray-300">|</span>
                    <span className="text-xs text-gray-400">GlowyAI (Gemini 2.5 Flash) ile oluşturuldu</span>
                  </div>
                </div>
              )}
            </div>

          </Section>
        )}

        {/* ── Section 8: Top Selling Products ──────────────────────────── */}
        <Section title="En Çok Satan Ürünler (Top 50)" dateLabel={activeDateLabel}>
          <SortableTable
            rows={[...itemSummary].filter((r) => Number(r.totalRevenue || r.total_revenue || 0) > 0).sort((a, b) => Number(b.totalRevenue || b.total_revenue || 0) - Number(a.totalRevenue || a.total_revenue || 0)).slice(0, 50)}
            columns={topItemColumns}
            filename="en-cok-satan-urunler.csv"
            dateLabel={activeDateLabel}
            initialSort={{ key: 'totalRevenue', dir: 'desc' }}
            isLoading={itemSummaryLoading}
            error={itemSummaryError}
          />
        </Section>

        {/* ── Campaign × Item × Revenue (LIVE GA4) — page bottom ────────── */}
        <Section title="Kampanya × Ürün × Ciro — Canlı GA4" dateLabel={activeDateLabel}>
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Canlı GA4 Data API
            </span>
            {campaignItemFetchedAt && (
              <span className="text-gray-500">
                Alındı: {campaignItemFetchedAt.toLocaleTimeString('tr-TR')}
              </span>
            )}
            <span className="text-gray-500">
              · sessionSource / sessionMedium / sessionCampaignName × itemName × itemRevenue
            </span>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {[
              { key: 'all', label: 'Tümü' },
              { key: 'google', label: 'Google Ads' },
              { key: 'meta', label: 'Meta Ads' },
              { key: 'tiktok', label: 'TikTok' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setCampaignItemPlatformFilter(f.key)}
                className={`px-3 py-1 text-xs font-medium rounded border ${
                  campaignItemPlatformFilter === f.key
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {f.label}
              </button>
            ))}
            <input
              type="text"
              placeholder="Kampanya veya ürün ara…"
              value={campaignItemQuery}
              onChange={(e) => setCampaignItemQuery(e.target.value)}
              className="text-xs border border-gray-300 rounded px-3 py-1 w-64 outline-none focus:border-gray-500"
            />
            <button
              onClick={() => {
                const filtered = filterCampaignItemRows(campaignItemRows, campaignItemPlatformFilter, campaignItemQuery);
                if (filtered.length === 0) return;
                const cols = [
                  { label: 'Source', key: 'source' },
                  { label: 'Medium', key: 'medium' },
                  { label: 'Kampanya', key: 'campaignName' },
                  { label: 'Ürün', key: 'itemName' },
                  { label: 'Ciro', key: 'revenue' },
                  { label: 'Adet', key: 'units' },
                ];
                const dateSuffix = (activeStartDate && activeEndDate) ? `_${activeStartDate}_${activeEndDate}` : '';
                exportCSV(filtered, cols, `kampanya-urun-ciro${dateSuffix}.csv`, activeDateLabel);
              }}
              disabled={campaignItemRows.length === 0}
              className="ml-auto inline-flex items-center gap-1.5 text-xs bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 px-3 py-1 rounded transition-colors"
            >
              <Download size={12} />
              CSV İndir
            </button>
          </div>

          {/* Body */}
          {campaignItemLoading ? (
            <LoadingSpinner />
          ) : campaignItemError ? (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
              {campaignItemError}
            </div>
          ) : campaignItemRows.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">Bu dönemde kampanya×ürün satış verisi bulunamadı.</p>
          ) : (
            <CampaignItemTable
              rows={campaignItemRows}
              platformFilter={campaignItemPlatformFilter}
              query={campaignItemQuery}
            />
          )}
        </Section>

        {/* ── Device Analysis (LIVE GA4) ─────────────────────────────── */}
        <Section title="Cihaz Analizi (Mobile / Desktop / Tablet)" dateLabel={activeDateLabel}>
          {deviceLoading ? (
            <LoadingSpinner />
          ) : deviceError ? (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">{deviceError}</div>
          ) : deviceRows.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">Bu dönem için cihaz verisi yok.</p>
          ) : (
            <div className="overflow-x-auto rounded border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Cihaz</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Oturum</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Kullanıcı</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Sipariş</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Ciro</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">CR (%)</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">AOV</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {deviceRows.map((d) => (
                    <tr key={d.device} className="border-t border-gray-100">
                      <td className="px-3 py-2 capitalize font-medium text-gray-800">{d.device}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{Number(d.sessions || 0).toLocaleString('tr-TR')}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{Number(d.users || 0).toLocaleString('tr-TR')}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{Number(d.transactions || 0).toLocaleString('tr-TR')}</td>
                      <td className="px-3 py-2 text-right text-gray-900">₺{Number(d.revenue || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
                      <td className="px-3 py-2 text-right text-gray-700">%{Number(d.cr || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-gray-700">₺{Number(d.aov || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* ── New vs Returning Revenue (LIVE GA4) ─────────────────────── */}
        <Section title="Yeni vs Dönen Müşteri Cirosu" dateLabel={activeDateLabel}>
          {newVsReturningLoading ? (
            <LoadingSpinner />
          ) : newVsReturningError ? (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">{newVsReturningError}</div>
          ) : !newVsReturning || !Array.isArray(newVsReturning.data) || newVsReturning.data.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">Bu dönem için veri yok.</p>
          ) : (() => {
              const newRow = newVsReturning.data.find((d) => String(d.userType || '').toLowerCase().includes('new')) || {};
              const retRow = newVsReturning.data.find((d) => String(d.userType || '').toLowerCase().includes('return')) || {};
              const newRev = Number(newRow.totalRevenue || 0);
              const retRev = Number(retRow.totalRevenue || 0);
              const newUsers = Number(newRow.totalUsers || 0);
              const retUsers = Number(retRow.totalUsers || 0);
              const totalRev = newRev + retRev;
              const newShare = totalRev > 0 ? (newRev / totalRev) * 100 : 0;
              const retShare = totalRev > 0 ? (retRev / totalRev) * 100 : 0;
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="border border-gray-200 rounded p-4 bg-white">
                    <div className="text-xs text-gray-500 uppercase tracking-wider">Yeni Müşteri Cirosu</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">₺{newRev.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-gray-600 mt-1">%{newShare.toFixed(1)} · {newUsers.toLocaleString('tr-TR')} kullanıcı</div>
                  </div>
                  <div className="border border-gray-200 rounded p-4 bg-white">
                    <div className="text-xs text-gray-500 uppercase tracking-wider">Dönen Müşteri Cirosu</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">₺{retRev.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-gray-600 mt-1">%{retShare.toFixed(1)} · {retUsers.toLocaleString('tr-TR')} kullanıcı</div>
                  </div>
                  <div className="border border-gray-200 rounded p-4 bg-white">
                    <div className="text-xs text-gray-500 uppercase tracking-wider">Toplam</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">₺{totalRev.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-gray-600 mt-1">{(newUsers + retUsers).toLocaleString('tr-TR')} kullanıcı</div>
                  </div>
                </div>
              );
            })()}
          <p className="text-xs text-gray-400 mt-2">
            Detaylı rapor: <a href="/analytics/new-vs-returning-revenue" className="underline text-indigo-600">/analytics/new-vs-returning-revenue</a>
          </p>
        </Section>

      </div>

      {/* ── Email Modal ──────────────────────────────────────────────── */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 print:hidden" onClick={() => !emailSending && setShowEmailModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Mail size={18} className="text-emerald-600" />
                Raporu Mail Gönder
              </h3>
              <button onClick={() => !emailSending && setShowEmailModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-gray-500 mb-4">
              Tüm bölümler ve CSV ekleri tek bir mail olarak gönderilecek. GlowyAI yorumları henüz oluşturulmamışsa otomatik olarak oluşturulup maile eklenecek.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Alıcı E-posta</label>
            <input
              type="email"
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              placeholder="ornek@email.com"
              disabled={emailSending}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50 mb-1"
            />
            <p className="text-[10px] text-gray-400 mb-4">Birden fazla alıcı virgülle ayırın.</p>

            {/* Sections summary */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs text-gray-600">
              <p className="font-medium text-gray-700 mb-1">Mail icerigi:</p>
              <ul className="space-y-0.5">
                <li>GA4 + Google Ads + Meta Ads kart ozeti</li>
                {aiSummary && <li>GlowyAI yonetici ozeti</li>}
                <li>Kampanya tablosu (top 30)</li>
                <li>Keyword tablosu (top 30)</li>
                {brandVsGenericKeywords && <li>Brand vs Generic ozet</li>}
                {bvgAnalysis && <li>Brand vs Generic AI analiz</li>}
                {itemsByProductLine.length > 0 && <li>Urun ailesi kirilimi</li>}
                {plAnalysis && <li>Urun ailesi AI analiz</li>}
                {creatives.length > 0 && <li>Meta kreatif performansi (top 20)</li>}
                <li>En cok satan urunler (top 20)</li>
              </ul>
              <p className="font-medium text-gray-700 mt-2 mb-1">CSV ekleri:</p>
              <ul className="space-y-0.5">
                <li>kampanya-performansi.csv</li>
                <li>anahtar-kelime-performansi.csv</li>
                {brandVsGenericKeywords && <li>brand-vs-generic-keywords.csv</li>}
                {creatives.length > 0 && <li>meta-kreatif-performansi.csv</li>}
                {itemsByProductLine.length > 0 && <li>urun-ailesi-kirilimi.csv</li>}
                <li>en-cok-satan-urunler.csv</li>
              </ul>
            </div>

            {/* Result message */}
            {emailResult && (
              <div className={`flex items-center gap-2 rounded-lg px-3 py-2 mb-4 text-sm ${emailResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {emailResult.success ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                <span>{emailResult.message}</span>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={() => sendReportEmail('email')}
                disabled={emailSending || !emailRecipient.trim()}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm px-4 py-2.5 rounded-xl shadow-sm transition-colors font-medium"
              >
                {emailSending ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    AI yorumları oluşturuluyor & gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    Gönder (AI Yorumları Dahil)
                  </>
                )}
              </button>
              <button
                onClick={() => setShowEmailModal(false)}
                disabled={emailSending}
                className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-xl transition-colors disabled:opacity-50"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
